import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { visitsAPI, servicesAPI, inventoryAPI, billsAPI, doctorsAPI, dashboardAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import ConsentDialog from '../components/ConsentDialog';
import ConsentForm from '../components/ConsentForm';
import DataPagination from '../components/DataPagination';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { 
  Stethoscope, 
  Pill, 
  ClipboardList,
  Send,
  Search,
  Clock,
  MapPin,
  Edit,
  UserCheck,
  CheckCircle,
  Receipt,
  DollarSign,
  History,
  RotateCcw,
  FileText,
  AlertCircle,
  UserCog,
  Trash2,
  XCircle
} from 'lucide-react';

const Visits = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, isClinicAdmin, isDoctor, isFrontDesk, workingLocation } = useAuth();
  
  // Check if user can edit prices (only doctors and admins, not front desk)
  const canEditPrices = isDoctor || isClinicAdmin || user?.role === 'super_admin' || user?.role === 'admin';
  
  const [visits, setVisits] = useState([]);
  const [filteredVisits, setFilteredVisits] = useState([]);
  const [services, setServices] = useState([]);
  const [searchedServices, setSearchedServices] = useState([]); // Services from server-side search
  const [servicesLoading, setServicesLoading] = useState(false);
  const [inventory, setInventory] = useState([]);
  const [searchedMedications, setSearchedMedications] = useState([]); // Medications from server-side search
  const [medicationsLoading, setMedicationsLoading] = useState(false);
  const [bills, setBills] = useState([]);
  const [doctorBills, setDoctorBills] = useState([]); // Bills created by this doctor
  const [doctors, setDoctors] = useState([]); // List of doctors for reassignment
  const [loading, setLoading] = useState(true);
  const [treatmentDialogOpen, setTreatmentDialogOpen] = useState(false);
  const [billEditDialogOpen, setBillEditDialogOpen] = useState(false);
  const [consentDialogOpen, setConsentDialogOpen] = useState(false);
  const [selectedVisit, setSelectedVisit] = useState(null);
  const [selectedBill, setSelectedBill] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [serviceSearch, setServiceSearch] = useState('');
  const [medicationSearch, setMedicationSearch] = useState('');
  const [serviceDropdownOpen, setServiceDropdownOpen] = useState(false);
  const [medicationDropdownOpen, setMedicationDropdownOpen] = useState(false);
  const [doctorViewTab, setDoctorViewTab] = useState('waiting'); // 'waiting', 'inprogress', 'history', 'manage', 'editBill', or 'consent'
  const [revisionReason, setRevisionReason] = useState('');
  const [showRevisionReasonDialog, setShowRevisionReasonDialog] = useState(false);
  const [activeManageTab, setActiveManageTab] = useState('services'); // For full-page manage view
  const [activeBillEditTab, setActiveBillEditTab] = useState('services'); // For full-page bill edit view
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  
  // Completed tab pagination state
  const [completedPage, setCompletedPage] = useState(1);
  const [completedPageSize, setCompletedPageSize] = useState(10);
  const [completedTotalItems, setCompletedTotalItems] = useState(0);
  const [completedTotalPages, setCompletedTotalPages] = useState(0);
  const [completedVisitsData, setCompletedVisitsData] = useState([]);
  const [loadingCompleted, setLoadingCompleted] = useState(false);
  
  // In Progress tab pagination state
  const [inProgressPage, setInProgressPage] = useState(1);
  const [inProgressPageSize, setInProgressPageSize] = useState(20);
  const [inProgressTotalItems, setInProgressTotalItems] = useState(0);
  const [inProgressTotalPages, setInProgressTotalPages] = useState(0);
  const [inProgressVisitsData, setInProgressVisitsData] = useState([]);
  const [loadingInProgress, setLoadingInProgress] = useState(false);
  
  const [treatmentForm, setTreatmentForm] = useState({
    services: [],
    medications: [],
    notes: ''
  });

  // Track which tabs have been loaded (for lazy loading)
  const [activeVisitsTab, setActiveVisitsTab] = useState(() => {
    const tab = searchParams.get('tab');
    return ['checked_in', 'in_progress', 'completed'].includes(tab) ? tab : 'checked_in';
  });
  const [tabsLoaded, setTabsLoaded] = useState({
    checked_in: false,
    in_progress: false,
    completed: false
  });
  
  // Refs to prevent duplicate API calls
  const fetchingInProgressRef = useRef(false);
  const fetchingCompletedRef = useRef(false);
  const fetchingCheckedInRef = useRef(false);
  const fetchingDataRef = useRef(false);
  const fetchingDoctorWaitingRef = useRef(false);
  const fetchingDoctorInProgressRef = useRef(false);
  const fetchingDoctorHistoryRef = useRef(false);

  // Doctor tab-specific state
  const [doctorWaitingVisits, setDoctorWaitingVisits] = useState([]);
  const [doctorInProgressVisitsData, setDoctorInProgressVisitsData] = useState([]);
  const [loadingDoctorWaiting, setLoadingDoctorWaiting] = useState(false);
  const [loadingDoctorInProgress, setLoadingDoctorInProgress] = useState(false);
  const [loadingDoctorHistory, setLoadingDoctorHistory] = useState(false);
  const [doctorTabsLoaded, setDoctorTabsLoaded] = useState({
    waiting: false,
    inprogress: false,
    history: false
  });
  
  // Initial count for in-progress badge (fetched from stats API on load)
  const [initialInProgressCount, setInitialInProgressCount] = useState(null);

  // Cancel visit state
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancellingVisit, setCancellingVisit] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [cancellingInProgress, setCancellingInProgress] = useState(false);


  // Bill edit form (same structure as treatmentForm)
  const [billEditForm, setBillEditForm] = useState({
    services: [],
    medications: [],
    notes: ''
  });

  useEffect(() => {
    if (!isDoctor()) {
      fetchCheckedInVisits();
      // If URL specifies a tab, also fetch that tab's data
      const urlTab = searchParams.get('tab');
      if (urlTab === 'in_progress') fetchInProgressVisits(true);
      else if (urlTab === 'completed') fetchCompletedVisits(true);
    } else {
      // Doctor: reset loaded flags and fetch current tab
      setDoctorTabsLoaded({ waiting: false, inprogress: false, history: false });
      if (doctorViewTab === 'waiting') fetchDoctorWaiting(true);
      else if (doctorViewTab === 'inprogress') fetchDoctorInProgress(true);
      else if (doctorViewTab === 'history') fetchDoctorHistory(true);
    }
  }, [workingLocation]);

  // Fetch only checked_in visits (for initial load and Waiting tab)
  const fetchCheckedInVisits = async () => {
    if (fetchingCheckedInRef.current) return; // Prevent duplicate calls
    
    fetchingCheckedInRef.current = true;
    try {
      setLoading(true);
      const params = { 
        page: currentPage, 
        page_size: pageSize,
        status: 'checked_in'
      };
      if (workingLocation?.id) {
        params.location_id = workingLocation.id;
      }
      
      const locationParams = workingLocation?.id ? { location_id: workingLocation.id } : {};
      
      const requests = [
        visitsAPI.getAll(params),
        // Fetch dashboard stats to get in-progress count for badge
        dashboardAPI.getStats(locationParams).catch(() => ({ data: {} }))
      ];
      
      // For front desk / admin, also fetch doctors for reassignment
      if (user?.role === 'front_desk' || user?.role === 'clinic_admin') {
        requests.push(doctorsAPI.getAll());
      }
      
      const results = await Promise.all(requests);
      const [visitsRes, statsRes] = results;
      
      const paginatedVisits = visitsRes.data;
      setVisits(paginatedVisits.items || []);
      setTotalItems(paginatedVisits.total || 0);
      setTotalPages(paginatedVisits.total_pages || 0);
      
      // Set the initial in-progress count from stats (includes pending_billing since that tab shows both)
      if (statsRes?.data) {
        const ipCount = (statsRes.data.in_progress || 0) + (statsRes.data.pending_billing || 0);
        setInitialInProgressCount(ipCount);
      }
      
      // Doctors array is now at index 2 (after statsRes)
      if ((user?.role === 'front_desk' || user?.role === 'clinic_admin') && results[2]) {
        const docData = results[2].data.items || results[2].data;
        setDoctors(Array.isArray(docData) ? docData : []);
      }
      
      setTabsLoaded(prev => ({ ...prev, checked_in: true }));
    } catch (error) {
      console.error('Failed to fetch checked-in visits:', error);
      toast.error('Failed to load visits');
    } finally {
      setLoading(false);
      fetchingCheckedInRef.current = false;
    }
  };

  // Fetch completed visits with separate pagination (for Front Desk/Admin view)
  const fetchCompletedVisits = async (forceRefresh = false) => {
    if (isDoctor()) return; // Doctors don't need this separate fetch
    if (tabsLoaded.completed && !forceRefresh) return; // Already loaded
    if (fetchingCompletedRef.current) return; // Prevent duplicate calls
    
    fetchingCompletedRef.current = true;
    setLoadingCompleted(true);
    try {
      const params = { 
        page: completedPage, 
        page_size: completedPageSize,
        status: 'completed'
      };
      if (workingLocation?.id) {
        params.location_id = workingLocation.id;
      }
      
      // Fetch visits for completed tab
      const visitsResponse = await visitsAPI.getAll(params);
      const paginatedData = visitsResponse.data;
      const visitsItems = paginatedData.items || [];
      setCompletedVisitsData(visitsItems);
      setCompletedTotalItems(paginatedData.total || 0);
      setCompletedTotalPages(paginatedData.total_pages || 0);
      
      // Bills are now embedded in visit data from backend - extract and add to bills state
      const embeddedBills = visitsItems
        .filter(v => v.bill)
        .map(v => v.bill);
      if (embeddedBills.length > 0) {
        setBills(prevBills => {
          const existingIds = new Set(prevBills.map(b => b.id));
          const newBills = embeddedBills.filter(b => !existingIds.has(b.id));
          return [...prevBills, ...newBills];
        });
      }
      
      setTabsLoaded(prev => ({ ...prev, completed: true }));
    } catch (error) {
      console.error('Failed to fetch completed visits:', error);
    } finally {
      setLoadingCompleted(false);
      fetchingCompletedRef.current = false;
    }
  };

  // Refetch completed when pagination changes (only if tab was already loaded)
  useEffect(() => {
    if (tabsLoaded.completed && activeVisitsTab === 'completed') {
      fetchCompletedVisits(true);
    }
  }, [completedPage, completedPageSize]);

  // Fetch in-progress visits with separate pagination (for Front Desk/Admin view)
  const fetchInProgressVisits = async (forceRefresh = false) => {
    if (isDoctor()) return; // Doctors don't need this separate fetch
    if (tabsLoaded.in_progress && !forceRefresh) return; // Already loaded
    if (fetchingInProgressRef.current) return; // Prevent duplicate calls
    
    fetchingInProgressRef.current = true;
    setLoadingInProgress(true);
    try {
      const params = { 
        page: inProgressPage, 
        page_size: inProgressPageSize,
        // Fetch both in_progress AND pending_billing (comma-separated)
        status: 'in_progress,pending_billing'
      };
      if (workingLocation?.id) {
        params.location_id = workingLocation.id;
      }
      
      const visitsResponse = await visitsAPI.getAll(params);
      const paginatedData = visitsResponse.data;
      const visitsItems = paginatedData.items || [];
      setInProgressVisitsData(visitsItems);
      setInProgressTotalItems(paginatedData.total || 0);
      setInProgressTotalPages(paginatedData.total_pages || 0);
      
      // Bills are now embedded in visit data from backend - extract and add to bills state
      const embeddedBills = visitsItems
        .filter(v => v.bill)
        .map(v => v.bill);
      if (embeddedBills.length > 0) {
        setBills(prevBills => {
          const existingIds = new Set(prevBills.map(b => b.id));
          const newBills = embeddedBills.filter(b => !existingIds.has(b.id));
          return [...prevBills, ...newBills];
        });
      }
      
      setTabsLoaded(prev => ({ ...prev, in_progress: true }));
    } catch (error) {
      console.error('Failed to fetch in-progress visits:', error);
    } finally {
      setLoadingInProgress(false);
      fetchingInProgressRef.current = false;
    }
  };

  // Refetch in-progress when pagination changes (only if tab was already loaded)
  useEffect(() => {
    if (tabsLoaded.in_progress && activeVisitsTab === 'in_progress') {
      fetchInProgressVisits(true);
    }
  }, [inProgressPage, inProgressPageSize]);

  // ==================== DOCTOR TAB-SPECIFIC FETCH FUNCTIONS ====================
  
  // Fetch waiting (checked_in) visits for doctor
  const fetchDoctorWaiting = async (forceRefresh = false) => {
    if (!isDoctor()) return;
    if (doctorTabsLoaded.waiting && !forceRefresh) return;
    if (fetchingDoctorWaitingRef.current) return;
    
    fetchingDoctorWaitingRef.current = true;
    setLoadingDoctorWaiting(true);
    try {
      const params = { 
        status: 'checked_in',
        page: 1,
        page_size: 100
      };
      // Don't filter by specific location - let backend use doctor's assigned locations
      // Doctor should see waiting patients across all their assigned locations
      
      const response = await visitsAPI.getAll(params);
      const items = response.data.items || [];
      // Show visits assigned to this doctor OR unclaimed visits
      const doctorItems = items.filter(v => 
        v.doctor_id === user?.id || (!v.doctor_id)
      );
      setDoctorWaitingVisits(doctorItems);
      setDoctorTabsLoaded(prev => ({ ...prev, waiting: true }));
    } catch (error) {
      console.error('Failed to fetch doctor waiting visits:', error);
    } finally {
      setLoadingDoctorWaiting(false);
      fetchingDoctorWaitingRef.current = false;
    }
  };

  // Fetch in-progress visits for doctor
  const fetchDoctorInProgress = async (forceRefresh = false) => {
    if (!isDoctor()) return;
    if (doctorTabsLoaded.inprogress && !forceRefresh) return;
    if (fetchingDoctorInProgressRef.current) return;
    
    fetchingDoctorInProgressRef.current = true;
    setLoadingDoctorInProgress(true);
    try {
      const params = { 
        status: 'in_progress',
        doctor_id: user?.id,
        page: 1,
        page_size: 100
      };
      // Don't filter by specific location - doctor sees all their in-progress across locations
      
      const response = await visitsAPI.getAll(params);
      const items = response.data.items || [];
      setDoctorInProgressVisitsData(items);
      setDoctorTabsLoaded(prev => ({ ...prev, inprogress: true }));
    } catch (error) {
      console.error('Failed to fetch doctor in-progress visits:', error);
    } finally {
      setLoadingDoctorInProgress(false);
      fetchingDoctorInProgressRef.current = false;
    }
  };

  // Fetch bill history for doctor (only editable: pending)
  const fetchDoctorHistory = async (forceRefresh = false) => {
    if (!isDoctor()) return;
    if (doctorTabsLoaded.history && !forceRefresh) return;
    if (fetchingDoctorHistoryRef.current) return;
    
    fetchingDoctorHistoryRef.current = true;
    setLoadingDoctorHistory(true);
    try {
      const response = await billsAPI.getByDoctor(user?.id, 'pending', workingLocation?.id);
      const editableBills = response.data.items || response.data || [];
      setDoctorBills(Array.isArray(editableBills) ? editableBills : []);
      setDoctorTabsLoaded(prev => ({ ...prev, history: true }));
    } catch (error) {
      console.error('Failed to fetch doctor bill history:', error);
    } finally {
      setLoadingDoctorHistory(false);
      fetchingDoctorHistoryRef.current = false;
    }
  };

  // Doctor tab change handler
  useEffect(() => {
    if (!isDoctor()) return;
    if (doctorViewTab === 'waiting') {
      fetchDoctorWaiting();
    } else if (doctorViewTab === 'inprogress') {
      fetchDoctorInProgress();
    } else if (doctorViewTab === 'history') {
      fetchDoctorHistory();
    }
  }, [doctorViewTab]);

  // Filter visits by search query and doctor
  useEffect(() => {
    let filtered = visits;
    
    // Filter by doctor if logged in as doctor
    // Show: visits assigned to this doctor OR checked_in visits without a doctor (available to claim)
    // Exclude completed visits that are already billed (not editable anymore)
    if (isDoctor()) {
      filtered = filtered.filter(v => {
        // Always show checked_in visits without a doctor (available to claim)
        if (v.status === 'checked_in' && !v.doctor_id) return true;
        
        // Only show visits assigned to this doctor
        if (v.doctor_id !== user?.id) return false;
        
        // For completed visits, only show if they don't have a completed bill
        // (i.e., they are still editable)
        if (v.status === 'completed') {
          // Check if the visit has any associated bills
          // If it has bills that are paid, don't show it
          const hasPaidBill = v.bills?.some(b => b.status === 'paid');
          return !hasPaidBill;
        }
        
        // Show all other visits (in_progress, pending_billing, etc.)
        return true;
      });
    }
    
    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(visit => 
        visit.patient_name?.toLowerCase().includes(query) ||
        visit.doctor_name?.toLowerCase().includes(query)
      );
    }
    
    setFilteredVisits(filtered);
  }, [searchQuery, visits, user, isDoctor]);

  const fetchData = async () => {
    if (fetchingDataRef.current) return; // Prevent duplicate calls
    
    fetchingDataRef.current = true;
    try {
      setLoading(true);
      const params = { page: currentPage, page_size: pageSize };
      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }
      if (workingLocation?.id) {
        params.location_id = workingLocation.id;
      }
      
      // For doctors, exclude completed visits that have been billed and paid
      if (isDoctor()) {
        params.exclude_billed_completed = true;
      }
      
      const requests = [
        visitsAPI.getAll(params),
        // Don't load all services upfront - use server-side search instead
        Promise.resolve({ data: { items: [] } }),
        // Don't load all inventory upfront - use server-side search instead
        Promise.resolve({ data: { items: [] } }),
        billsAPI.getAll(workingLocation?.id ? { location_id: workingLocation.id, page_size: 100 } : { page_size: 100 })
      ];
      
      // If doctor, also fetch their bill history
      if (user?.role === 'doctor' && user?.id) {
        requests.push(billsAPI.getByDoctor(user.id));
      }
      
      // For front desk / admin, also fetch doctors for reassignment
      if (user?.role === 'front_desk' || user?.role === 'clinic_admin') {
        requests.push(doctorsAPI.getAll()); // Get ALL doctors, not filtered by location
      }
      
      const results = await Promise.all(requests);
      const [visitsRes, servicesRes, inventoryRes, billsRes] = results;
      
      // Handle paginated visits response
      const paginatedVisits = visitsRes.data;
      setVisits(paginatedVisits.items || []);
      setTotalItems(paginatedVisits.total || 0);
      setTotalPages(paginatedVisits.total_pages || 0);
      
      // Handle other paginated responses
      setServices(servicesRes.data.items || servicesRes.data || []);
      setInventory(inventoryRes.data.items || inventoryRes.data || []);
      setBills(billsRes.data.items || billsRes.data || []);
      
      if (user?.role === 'doctor' && results[4]) {
        const docBillsData = results[4].data.items || results[4].data;
        setDoctorBills(Array.isArray(docBillsData) ? docBillsData : []);
      }
      
      // Set doctors for front desk / admin (ALL doctors)
      if ((user?.role === 'front_desk' || user?.role === 'clinic_admin') && results[4]) {
        const docData = results[4].data.items || results[4].data;
        setDoctors(Array.isArray(docData) ? docData : []);
      }
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
      fetchingDataRef.current = false;
    }
  };

  // Refresh all loaded tabs (used after status changes)
  const refreshAllTabs = async () => {
    if (isDoctor()) {
      // Reset loaded flags so tabs re-fetch
      setDoctorTabsLoaded({ waiting: false, inprogress: false, history: false });
      // Refresh the current active tab
      if (doctorViewTab === 'waiting') await fetchDoctorWaiting(true);
      else if (doctorViewTab === 'inprogress') await fetchDoctorInProgress(true);
      else if (doctorViewTab === 'history') await fetchDoctorHistory(true);
      return;
    }
    
    // Refresh checked_in (always loaded)
    await fetchCheckedInVisits();
    
    // Refresh other tabs only if they were already loaded
    if (tabsLoaded.in_progress) {
      await fetchInProgressVisits(true);
    }
    if (tabsLoaded.completed) {
      await fetchCompletedVisits(true);
    }
  };

  const openCancelDialog = (visit) => {
    setCancellingVisit(visit);
    setCancelReason('');
    setCancelDialogOpen(true);
  };

  const handleCancelVisit = async () => {
    if (!cancellingVisit || !cancelReason.trim()) return;
    setCancellingInProgress(true);
    try {
      await visitsAPI.cancelVisit(cancellingVisit.id, cancelReason.trim());
      toast.success('Visit cancelled successfully');
      setCancelDialogOpen(false);
      setCancellingVisit(null);
      refreshAllTabs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel visit');
    } finally {
      setCancellingInProgress(false);
    }
  };


  // Debounced server-side service search
  useEffect(() => {
    const searchServices = async () => {
      if (!serviceSearch || serviceSearch.length < 2) {
        setSearchedServices([]);
        return;
      }
      
      setServicesLoading(true);
      try {
        const response = await servicesAPI.getAll({ 
          search: serviceSearch, 
          page_size: 20 
        });
        const results = response.data.items || response.data || [];
        // Filter out already added services
        const filtered = results.filter(s => 
          !treatmentForm.services.some(ts => ts.service_id === s.id)
        );
        setSearchedServices(filtered);
      } catch (error) {
        console.error('Error searching services:', error);
        setSearchedServices([]);
      }
      setServicesLoading(false);
    };

    const debounceTimer = setTimeout(searchServices, 300);
    return () => clearTimeout(debounceTimer);
  }, [serviceSearch, treatmentForm.services]);

  // Debounced server-side medication search
  useEffect(() => {
    const searchMedications = async () => {
      if (!medicationSearch || medicationSearch.length < 2) {
        setSearchedMedications([]);
        return;
      }
      
      setMedicationsLoading(true);
      try {
        // Filter medications by location
        // Priority: working location > visit's location
        // This ensures doctors only see inventory from their assigned locations
        const params = { 
          search: medicationSearch, 
          page_size: 20 
        };
        
        // Use working location if available (respects user's assigned locations)
        // Fall back to visit's location if no working location
        if (workingLocation?.id) {
          params.location_id = workingLocation.id;
        } else if (selectedVisit?.location_id) {
          params.location_id = selectedVisit.location_id;
        }
        
        const response = await inventoryAPI.getAll(params);
        const results = response.data.items || response.data || [];
        // Filter out already added medications but KEEP out-of-stock items visible
        // This allows doctors to see what's in inventory even if out of stock
        const filtered = results.filter(i => 
          !treatmentForm.medications.some(tm => tm.inventory_id === i.id)
        );
        // Sort: in-stock items first, then out-of-stock
        filtered.sort((a, b) => {
          const stockA = a.quantity_on_hand || a.quantity || 0;
          const stockB = b.quantity_on_hand || b.quantity || 0;
          if (stockA > 0 && stockB === 0) return -1;
          if (stockA === 0 && stockB > 0) return 1;
          return 0;
        });
        setSearchedMedications(filtered);
      } catch (error) {
        console.error('Error searching medications:', error);
        setSearchedMedications([]);
      }
      setMedicationsLoading(false);
    };

    const debounceTimer = setTimeout(searchMedications, 300);
    return () => clearTimeout(debounceTimer);
  }, [medicationSearch, treatmentForm.medications, workingLocation?.id, selectedVisit?.location_id]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  // Get bill for a visit
  const getBillForVisit = (visitId) => {
    return bills.find(b => b.visit_id === visitId);
  };

  const openTreatmentDialog = async (visit) => {
    setSelectedVisit(visit);
    
    // Load medications and fetch current stock levels
    let medicationsWithStock = visit.medications || [];
    
    if (medicationsWithStock.length > 0 && visit.location_id) {
      try {
        // Fetch current inventory to get stock levels using the inventoryAPI
        const res = await inventoryAPI.getAll({ 
          location_id: visit.location_id,
          page_size: 500 
        });
        const inventoryItems = res.data.items || res.data || [];
        
        // Map inventory stock to medications
        medicationsWithStock = medicationsWithStock.map(med => {
          const invItem = inventoryItems.find(i => i.id === med.inventory_id);
          return {
            ...med,
            available_stock: invItem ? (invItem.quantity_on_hand || invItem.quantity || 0) : (med.available_stock || 0)
          };
        });
      } catch (err) {
        console.warn('Could not fetch inventory for stock levels:', err);
      }
    }
    
    setTreatmentForm({
      services: visit.services || [],
      medications: medicationsWithStock,
      notes: visit.notes || ''
    });
    setServiceSearch('');
    setMedicationSearch('');
    setActiveManageTab('services');
    // For doctors, use full-page view instead of dialog
    if (isDoctor()) {
      setDoctorViewTab('manage');
    } else {
      setTreatmentDialogOpen(true);
    }
  };

  const closeManagePatientView = () => {
    setDoctorViewTab('patients');
    setSelectedVisit(null);
    setServiceSearch('');
    setMedicationSearch('');
    setServiceDropdownOpen(false);
    setMedicationDropdownOpen(false);
  };

  const addService = (serviceId) => {
    const service = searchedServices.find(s => s.id === serviceId);
    if (!service || treatmentForm.services.some(s => s.service_id === serviceId)) return;
    
    const price = service.price || 0;
    const isAdjustable = service.category === 'Adjustable Procedure';
    setTreatmentForm({
      ...treatmentForm,
      services: [...treatmentForm.services, {
        service_id: service.id,
        service_name: service.name,
        category: service.category,
        quantity: 1,
        unit_price: price,
        total: price,
        notes: '',
        is_adjustable: isAdjustable
      }]
    });
    setServiceSearch('');
    setSearchedServices([]); // Clear search results
    setServiceDropdownOpen(false);
  };

  const updateServiceQuantity = (index, quantity) => {
    const updated = [...treatmentForm.services];
    updated[index].quantity = parseInt(quantity) || 1;
    updated[index].total = updated[index].quantity * updated[index].unit_price;
    setTreatmentForm({ ...treatmentForm, services: updated });
  };

  const updateServicePrice = (index, price) => {
    const updated = [...treatmentForm.services];
    // Remove leading zeros and parse the price
    const cleanPrice = price.replace(/^0+(?=\d)/, '');
    updated[index].unit_price = parseFloat(cleanPrice) || 0;
    updated[index].total = updated[index].quantity * updated[index].unit_price;
    setTreatmentForm({ ...treatmentForm, services: updated });
  };

  const removeService = (index) => {
    setTreatmentForm({
      ...treatmentForm,
      services: treatmentForm.services.filter((_, i) => i !== index)
    });
  };

  const addMedication = (inventoryId) => {
    const item = searchedMedications.find(i => i.id === inventoryId);
    if (!item || treatmentForm.medications.some(m => m.inventory_id === inventoryId)) return;
    
    const stock = item.quantity || item.quantity_on_hand || 0;
    if (stock < 1) {
      toast.error(`${item.name} is out of stock`);
      return;
    }
    
    const price = item.unit_price || 0;
    setTreatmentForm({
      ...treatmentForm,
      medications: [...treatmentForm.medications, {
        inventory_id: item.id,
        medication_name: item.name,
        quantity: 1,
        unit_price: price,
        total: price,
        notes: '',
        available_stock: stock // Store available stock for validation
      }]
    });
    setMedicationSearch('');
    setSearchedMedications([]); // Clear search results
    setMedicationDropdownOpen(false);
  };

  const updateMedicationQuantity = (index, quantity) => {
    const updated = [...treatmentForm.medications];
    const med = updated[index];
    // Use stored available_stock from when item was added
    const availableStock = med.available_stock || 999;
    
    let newQuantity = parseInt(quantity) || 1;
    
    // Don't allow more than available stock
    if (newQuantity > availableStock) {
      toast.error(`Only ${availableStock} units available for ${med.medication_name}`);
      newQuantity = availableStock;
    }
    
    // Ensure minimum of 1
    if (newQuantity < 1) {
      newQuantity = 1;
    }
    
    updated[index].quantity = newQuantity;
    updated[index].total = newQuantity * updated[index].unit_price;
    setTreatmentForm({ ...treatmentForm, medications: updated });
  };

  const removeMedication = (index) => {
    setTreatmentForm({
      ...treatmentForm,
      medications: treatmentForm.medications.filter((_, i) => i !== index)
    });
  };

  const updateMedicationPrice = (index, price) => {
    const updated = [...treatmentForm.medications];
    // Remove leading zeros and parse the price
    const cleanPrice = typeof price === 'string' ? price.replace(/^0+(?=\d)/, '') : price;
    updated[index].unit_price = parseFloat(cleanPrice) || 0;
    updated[index].total = updated[index].quantity * updated[index].unit_price;
    setTreatmentForm({ ...treatmentForm, medications: updated });
  };

  const handleSaveTreatment = async () => {
    try {
      await visitsAPI.update(selectedVisit.id, {
        services: treatmentForm.services,
        medications: treatmentForm.medications,
        notes: treatmentForm.notes,
        status: 'in_progress'
      });
      toast.success('Treatment saved');
      setTreatmentDialogOpen(false);
      // Navigate back to patients tab for doctor
      if (isDoctor()) {
        closeManagePatientView();
      }
      refreshAllTabs();
    } catch (error) {
      toast.error('Failed to save treatment');
    }
  };

  // Open full-page consent form (for doctors)
  const openConsentForm = () => {
    // Validate medication quantities against available stock (stored when item was added)
    for (const med of treatmentForm.medications) {
      const availableStock = med.available_stock || 0;
      
      if (med.quantity > availableStock) {
        toast.error(`Cannot submit: ${med.medication_name} only has ${availableStock} units available, but ${med.quantity} requested`);
        return;
      }
    }
    
    // Check if there's anything to bill
    if (treatmentForm.services.length === 0 && treatmentForm.medications.length === 0) {
      toast.error('Please add at least one service or medication before submitting');
      return;
    }
    
    // Switch to full-page consent form
    setDoctorViewTab('consent');
  };

  // Open consent dialog (for non-doctor roles using the dialog)
  const openConsentDialog = () => {
    // Validate medication quantities against available stock (stored when item was added)
    for (const med of treatmentForm.medications) {
      const availableStock = med.available_stock || 0;
      
      if (med.quantity > availableStock) {
        toast.error(`Cannot submit: ${med.medication_name} only has ${availableStock} units available, but ${med.quantity} requested`);
        return;
      }
    }
    
    // Check if there's anything to bill
    if (treatmentForm.services.length === 0 && treatmentForm.medications.length === 0) {
      toast.error('Please add at least one service or medication before submitting');
      return;
    }
    
    setConsentDialogOpen(true);
  };

  const [submittingForBilling, setSubmittingForBilling] = useState(false);
  
  const handleSubmitForBilling = async (consentData) => {
    if (submittingForBilling) return;
    setSubmittingForBilling(true);
    try {
      // First save the treatment data with consent information
      await visitsAPI.update(selectedVisit.id, {
        services: treatmentForm.services,
        medications: treatmentForm.medications,
        notes: treatmentForm.notes,
        status: 'in_progress',
        consent: consentData  // Store consent data
      });
      
      // Now submit for billing - this auto-creates the bill
      await visitsAPI.submitForBilling(selectedVisit.id);
      
      toast.success('Consent signed & bill created - Front desk can now process payment');
      setTreatmentDialogOpen(false);
      setConsentDialogOpen(false);
      // Navigate back to patients tab for doctor
      if (isDoctor()) {
        closeManagePatientView();
      }
      refreshAllTabs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit for billing');
    } finally {
      setSubmittingForBilling(false);
    }
  };

  const canManageTreatment = (visit) => {
    if (isClinicAdmin()) return true;
    if (isDoctor() && visit.doctor_id === user?.id) return true;
    return false;
  };

  // Close visit without billing (for Nursing Only visits)
  const closeVisitWithoutBilling = async (visit) => {
    if (!window.confirm(`Are you sure you want to close this Nursing Only visit for ${visit.patient_name}? No bill will be generated.`)) {
      return;
    }
    
    try {
      await visitsAPI.update(visit.id, {
        status: 'completed',
        notes: visit.notes || 'Nursing Only visit - closed without billing'
      });
      toast.success(`Visit closed successfully for ${visit.patient_name}`);
      refreshAllTabs();
    } catch (error) {
      toast.error('Failed to close visit');
      console.error('Error closing visit:', error);
    }
  };

  // Check if visit is Nursing Only type
  const isNursingOnlyVisit = (visit) => {
    return visit.visit_type === 'NO';
  };

  // Filter visits by status for tabs (Front Desk/Admin view)
  const searchFilter = (v) => !searchQuery.trim() || v.patient_name?.toLowerCase().includes(searchQuery.toLowerCase()) || v.doctor_name?.toLowerCase().includes(searchQuery.toLowerCase());
  const checkedInPatients = isDoctor() 
    ? doctorWaitingVisits.filter(searchFilter)
    : filteredVisits.filter(v => v.status === 'checked_in');
  const inProgressVisits = inProgressVisitsData.filter(searchFilter);
  const completedVisits = completedVisitsData.filter(searchFilter);
  const doctorInProgressPatients = isDoctor() 
    ? doctorInProgressVisitsData.filter(searchFilter) 
    : inProgressVisitsData.filter(searchFilter);
  const doctorPatients = [...checkedInPatients, ...doctorInProgressPatients];

  // Treatment Dialog - defined here so it's available for both Doctor and Front Desk views
  const renderTreatmentDialog = () => {
    // Use server-side searched services (no client-side filtering needed)
    const filteredServices = searchedServices;
    
    // Use server-side searched medications (no client-side filtering needed)
    const filteredMedications = searchedMedications;

    const treatmentTotal = treatmentForm.services.reduce((sum, s) => sum + (s.total || 0), 0) + 
                          treatmentForm.medications.reduce((sum, m) => sum + (m.total || 0), 0);

    return (
      <Dialog open={treatmentDialogOpen} onOpenChange={(open) => {
        setTreatmentDialogOpen(open);
        if (!open) {
          setServiceSearch('');
          setMedicationSearch('');
        }
      }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
          {/* Compact Header */}
          <div className="flex items-center justify-between pb-3 border-b border-gray-100">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center">
                <span className="text-base font-bold text-teal-700">
                  {selectedVisit?.patient_name?.split(' ').map(n => n[0]).join('').slice(0, 2)}
                </span>
              </div>
              <div>
                <h2 className="text-lg font-bold text-gray-900">{selectedVisit?.patient_name}</h2>
                <p className="text-gray-500 text-xs">{selectedVisit?.location_name}</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-xl font-bold text-teal-600">${treatmentTotal.toFixed(2)}</p>
              <p className="text-xs text-gray-500">Total</p>
            </div>
          </div>
          
          <DialogBody className="flex-1 overflow-y-auto py-3">
            <Tabs defaultValue="services" className="h-full">
              <TabsList className="grid w-full grid-cols-3 bg-gray-100 p-1 rounded-lg h-11 mb-4">
                <TabsTrigger value="services" className="data-[state=active]:bg-white rounded-md text-sm">
                  <Stethoscope className="w-4 h-4 mr-1.5" />
                  Services
                  {treatmentForm.services.length > 0 && (
                    <span className="ml-1.5 px-1.5 py-0.5 bg-teal-100 text-teal-700 text-xs rounded-full font-semibold">
                      {treatmentForm.services.length}
                    </span>
                  )}
                </TabsTrigger>
                <TabsTrigger value="medications" className="data-[state=active]:bg-white rounded-md text-sm">
                  <Pill className="w-4 h-4 mr-1.5" />
                  Meds
                  {treatmentForm.medications.length > 0 && (
                    <span className="ml-1.5 px-1.5 py-0.5 bg-teal-100 text-teal-700 text-xs rounded-full font-semibold">
                      {treatmentForm.medications.length}
                    </span>
                  )}
                </TabsTrigger>
                <TabsTrigger value="notes" className="data-[state=active]:bg-white rounded-md text-sm">
                  <ClipboardList className="w-4 h-4 mr-1.5" />
                  Notes
                </TabsTrigger>
              </TabsList>
              
              {/* Services Tab */}
              <TabsContent value="services" className="space-y-3 mt-0">
                {/* Searchable Dropdown for Services */}
                <div className="relative">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      placeholder="Type to search services (min 2 chars)..."
                      value={serviceSearch}
                      onChange={(e) => setServiceSearch(e.target.value)}
                      className="pl-9 h-10 text-sm bg-gray-50 border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500"
                    />
                  </div>
                  
                  {/* Dropdown Results */}
                  {serviceSearch.length >= 2 && (
                    <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-80 overflow-y-auto">
                      {servicesLoading ? (
                        <div className="px-3 py-4 text-center text-gray-500 text-sm">
                          <div className="animate-spin inline-block w-4 h-4 border-2 border-teal-500 border-t-transparent rounded-full mr-2"></div>
                          Searching...
                        </div>
                      ) : filteredServices.length > 0 ? (
                        filteredServices.map((s) => (
                          <div
                            key={s.id}
                            onClick={() => { addService(s.id); setServiceSearch(''); }}
                            className="flex items-center justify-between px-3 py-2.5 hover:bg-teal-50 cursor-pointer border-b border-gray-100 last:border-0"
                          >
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-gray-900 text-sm">{s.name}</span>
                              {s.category === 'Adjustable Procedure' && (
                                <span className="px-1.5 py-0.5 text-[10px] font-semibold bg-amber-100 text-amber-700 rounded uppercase tracking-wide">Custom Price</span>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              {s.category === 'Adjustable Procedure' && (
                                <svg className="w-4 h-4 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                </svg>
                              )}
                              <span className="text-teal-600 font-semibold text-sm">${(s.price || 0).toFixed(2)}</span>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="px-3 py-4 text-center text-gray-500 text-sm">
                          No services found for "{serviceSearch}"
                        </div>
                      )}
                    </div>
                  )}
                </div>
                
                {/* Added Services List */}
                <div className="space-y-2 max-h-[280px] overflow-y-auto">
                  {treatmentForm.services.length === 0 ? (
                    <div className="text-center py-8 text-gray-400">
                      <Stethoscope className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">No services added yet</p>
                      <p className="text-xs">Search above to add services</p>
                    </div>
                  ) : (
                    treatmentForm.services.map((s, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-gray-900 text-sm truncate">{s.service_name}</p>
                            {s.is_adjustable && (
                              <span className="px-1.5 py-0.5 text-[10px] font-semibold bg-amber-100 text-amber-700 rounded uppercase tracking-wide">Custom Price</span>
                            )}
                          </div>
                          {/* Editable prices for doctors and admins only */}
                          {canEditPrices ? (
                            <div className="flex items-center gap-2 mt-1.5">
                              <div className="relative flex items-center">
                                <span className={`absolute left-2 font-medium text-sm ${s.is_adjustable ? 'text-amber-600' : 'text-teal-600'}`}>$</span>
                                <input
                                  type="text"
                                  inputMode="decimal"
                                  value={s.unit_price || ''}
                                  onChange={(e) => {
                                    const val = e.target.value.replace(/[^0-9.]/g, '');
                                    updateServicePrice(idx, val);
                                  }}
                                  className={`w-24 h-8 pl-5 pr-2 text-sm font-semibold border-2 rounded-lg focus:outline-none focus:ring-2 transition-all ${
                                    s.is_adjustable 
                                      ? 'text-amber-800 border-amber-300 bg-amber-50 focus:ring-amber-400 focus:border-amber-400' 
                                      : 'text-teal-800 border-teal-300 bg-teal-50 focus:ring-teal-400 focus:border-teal-400'
                                  }`}
                                />
                              </div>
                              <span className="text-xs text-gray-500">per unit</span>
                            </div>
                          ) : (
                            <p className="text-xs text-gray-500 mt-0.5">${(s.unit_price || 0).toFixed(2)} each</p>
                          )}
                        </div>
                        <div className="flex items-center gap-2 ml-2">
                          <div className="flex items-center bg-white rounded border border-gray-200">
                            <button onClick={() => updateServiceQuantity(idx, Math.max(1, s.quantity - 1))} className="px-2 py-1 text-gray-600 hover:text-gray-900 text-sm">-</button>
                            <span className="px-2 py-1 font-semibold text-gray-900 text-sm min-w-[28px] text-center">{s.quantity}</span>
                            <button onClick={() => updateServiceQuantity(idx, s.quantity + 1)} className="px-2 py-1 text-gray-600 hover:text-gray-900 text-sm">+</button>
                          </div>
                          <span className="font-bold text-gray-900 text-sm min-w-[60px] text-right">${(s.total || 0).toFixed(2)}</span>
                          <button onClick={() => removeService(idx)} className="p-1 text-rose-500 hover:bg-rose-50 rounded">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </TabsContent>
              
              {/* Medications Tab */}
              <TabsContent value="medications" className="space-y-3 mt-0">
                {/* Searchable Dropdown for Medications */}
                <div className="relative">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      placeholder="Type to search medications (min 2 chars)..."
                      value={medicationSearch}
                      onChange={(e) => setMedicationSearch(e.target.value)}
                      className="pl-9 h-10 text-sm bg-gray-50 border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500"
                    />
                  </div>
                  
                  {/* Dropdown Results */}
                  {medicationSearch.length >= 2 && (
                    <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-80 overflow-y-auto">
                      {medicationsLoading ? (
                        <div className="px-3 py-4 text-center text-gray-500 text-sm">
                          <div className="animate-spin inline-block w-4 h-4 border-2 border-teal-500 border-t-transparent rounded-full mr-2"></div>
                          Searching...
                        </div>
                      ) : filteredMedications.length > 0 ? (
                        filteredMedications.map((i) => {
                          const stock = i.quantity_on_hand || i.quantity || 0;
                          const isOutOfStock = stock === 0;
                          const isLowStock = stock > 0 && stock <= (i.reorder_threshold || 10);
                          return (
                            <div
                              key={i.id}
                              onClick={() => {
                                if (!isOutOfStock) {
                                  addMedication(i.id);
                                  setMedicationSearch('');
                                }
                              }}
                              className={`flex items-center justify-between px-3 py-2.5 border-b border-gray-100 last:border-0 ${
                                isOutOfStock 
                                  ? 'bg-gray-50 cursor-not-allowed opacity-70' 
                                  : 'hover:bg-teal-50 cursor-pointer'
                              }`}
                            >
                              <div className="flex items-center gap-2">
                                <span className={`font-medium text-sm ${isOutOfStock ? 'text-gray-500' : 'text-gray-900'}`}>{i.name}</span>
                                <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${
                                  isOutOfStock 
                                    ? 'bg-red-100 text-red-700' 
                                    : isLowStock 
                                      ? 'bg-amber-100 text-amber-700' 
                                      : 'bg-emerald-100 text-emerald-700'
                                }`}>
                                  {isOutOfStock ? 'Out of Stock' : `${stock} in stock`}
                                </span>
                              </div>
                              <span className={`font-semibold text-sm ${isOutOfStock ? 'text-gray-400' : 'text-teal-600'}`}>
                                ${(i.unit_price || 0).toFixed(2)}
                              </span>
                            </div>
                          );
                        })
                      ) : (
                        <div className="px-3 py-4 text-center text-gray-500 text-sm">
                          No medications found for "{medicationSearch}"
                        </div>
                      )}
                    </div>
                  )}
                </div>
                
                {/* Added Medications List */}
                <div className="space-y-2 max-h-[280px] overflow-y-auto">
                  {treatmentForm.medications.length === 0 ? (
                    <div className="text-center py-8 text-gray-400">
                      <Pill className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">No medications added yet</p>
                      <p className="text-xs">Search above to add medications</p>
                    </div>
                  ) : (
                    treatmentForm.medications.map((m, idx) => {
                      const availableStock = m.available_stock || 0;
                      const isOverStock = m.quantity > availableStock;
                      return (
                        <div key={idx} className={`flex items-center justify-between p-3 rounded-lg border ${isOverStock ? 'bg-rose-50 border-rose-200' : 'bg-gray-50 border-gray-200'}`}>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-gray-900 text-sm truncate">{m.medication_name}</p>
                            <div className="flex items-center gap-2 mt-1">
                            {canEditPrices ? (
                              <div className="relative flex items-center">
                                <span className="absolute left-2 text-teal-600 font-medium text-xs">$</span>
                                <input
                                  type="text"
                                  inputMode="decimal"
                                  value={m.unit_price || ''}
                                  onChange={(e) => {
                                    const val = e.target.value.replace(/[^0-9.]/g, '');
                                    updateMedicationPrice(idx, val);
                                  }}
                                  className="w-20 h-7 pl-4 pr-2 text-xs text-teal-800 font-semibold border border-teal-300 rounded bg-teal-50 focus:outline-none focus:ring-1 focus:ring-teal-400 focus:border-teal-400 transition-all"
                                />
                              </div>
                            ) : (
                              <span className="text-xs text-gray-600 font-medium">${(m.unit_price || 0).toFixed(2)}</span>
                            )}
                              <span className={`text-xs px-1.5 py-0.5 rounded ${isOverStock ? 'bg-rose-100 text-rose-700' : 'bg-emerald-100 text-emerald-700'}`}>
                                {availableStock} available
                              </span>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 ml-2">
                            <div className="flex items-center bg-white rounded border border-gray-200">
                              <button onClick={() => updateMedicationQuantity(idx, Math.max(1, m.quantity - 1))} className="px-2 py-1 text-gray-600 hover:text-gray-900 text-sm">-</button>
                              <span className={`px-2 py-1 font-semibold text-sm min-w-[28px] text-center ${isOverStock ? 'text-rose-600' : 'text-gray-900'}`}>{m.quantity}</span>
                              <button onClick={() => updateMedicationQuantity(idx, m.quantity + 1)} className="px-2 py-1 text-gray-600 hover:text-gray-900 text-sm">+</button>
                            </div>
                            <span className="font-bold text-gray-900 text-sm min-w-[60px] text-right">${(m.total || 0).toFixed(2)}</span>
                            <button onClick={() => removeMedication(idx)} className="p-1 text-rose-500 hover:bg-rose-50 rounded">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                          </button>
                        </div>
                      </div>
                      );
                    })
                  )}
                </div>
              </TabsContent>
              
              {/* Notes Tab */}
              <TabsContent value="notes" className="mt-0">
                <Textarea
                  value={treatmentForm.notes}
                  onChange={(e) => setTreatmentForm({ ...treatmentForm, notes: e.target.value })}
                  placeholder="Add clinical notes about this visit..."
                  rows={6}
                  className="text-sm bg-gray-50 border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 resize-none"
                  data-testid="visit-notes"
                />
              </TabsContent>
            </Tabs>
          </DialogBody>
          
          {/* Compact Footer */}
          <div className="pt-3 border-t border-gray-100">
            <div className="flex items-center justify-between mb-3 px-3 py-2 bg-gray-50 rounded-lg">
              <div className="flex gap-4 text-sm">
                <span className="text-gray-500">Services: <span className="font-semibold text-gray-900">${treatmentForm.services.reduce((sum, s) => sum + (s.total || 0), 0).toFixed(2)}</span></span>
                <span className="text-gray-500">Meds: <span className="font-semibold text-gray-900">${treatmentForm.medications.reduce((sum, m) => sum + (m.total || 0), 0).toFixed(2)}</span></span>
              </div>
              <span className="text-lg font-bold text-teal-600">${treatmentTotal.toFixed(2)}</span>
            </div>
            
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setTreatmentDialogOpen(false)} className="flex-1 h-10 text-sm rounded-lg">
                Cancel
              </Button>
              <Button variant="outline" onClick={handleSaveTreatment} className="flex-1 h-10 text-sm rounded-lg" data-testid="save-treatment-btn">
                Save Progress
              </Button>
              <Button onClick={openConsentDialog} className="flex-1 h-10 text-sm bg-teal-600 hover:bg-teal-700 text-white rounded-lg" data-testid="submit-billing-btn">
                <Send className="w-4 h-4 mr-1.5" />
                Submit
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  };

  // Helper function for status badge styling
  const getStatusBadgeStyle = (status) => {
    switch (status) {
      case 'checked_in':
        return 'bg-teal-100 text-teal-700';
      case 'in_progress':
        return 'bg-amber-100 text-amber-700';
      case 'pending_billing':
        return 'bg-rose-100 text-rose-700';
      case 'completed':
        return 'bg-gray-100 text-gray-600';
      default:
        return 'bg-gray-100 text-gray-600';
    }
  };

  // Helper function for visit type badge
  const getVisitTypeBadge = (visitType) => {
    const types = {
      'FU': { label: 'Follow Up', style: 'bg-blue-100 text-blue-700' },
      'NP': { label: 'New Patient', style: 'bg-green-100 text-green-700' },
      'NC': { label: 'New Consultation', style: 'bg-purple-100 text-purple-700' },
      'LR': { label: 'Lab Results', style: 'bg-amber-100 text-amber-700' },
      'US': { label: 'Ultrasound', style: 'bg-cyan-100 text-cyan-700' },
      'NO': { label: 'Nursing Only', style: 'bg-rose-100 text-rose-700' },
      'PH': { label: 'Pharmacy', style: 'bg-teal-100 text-teal-700' }
    };
    return types[visitType] || { label: visitType || 'Unknown', style: 'bg-gray-100 text-gray-600' };
  };

  // Reassign doctor to a visit (Front Desk only)
  const handleReassignDoctor = async (visitId, doctorId) => {
    try {
      await visitsAPI.assignDoctor(visitId, doctorId);
      toast.success('Doctor reassigned successfully');
      refreshAllTabs(); // Refresh data
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reassign doctor');
    }
  };

  // Navigate to billing page
  const goToBilling = () => {
    navigate('/billing?tab=pending');
  };

  // Render accordion item for a visit
  const renderVisitAccordion = (visit) => {
    const isPendingBilling = visit.status === 'pending_billing';
    
    return (
      <AccordionItem 
        key={visit.id} 
        value={visit.id}
        className={`border rounded-lg px-4 bg-white shadow-sm ${isPendingBilling ? 'border-l-4 border-l-rose-500' : ''}`}
        data-testid={`visit-row-${visit.id}`}
      >
        <AccordionTrigger className="hover:no-underline py-4">
          <div className="flex items-center justify-between w-full pr-4">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-full flex items-center justify-center bg-gray-100">
                <span className="text-sm font-medium text-gray-600">
                  {visit.patient_name?.split(' ').map(n => n[0]).join('').slice(0, 2)}
                </span>
              </div>
              <div className="text-left">
                <p className="font-medium text-gray-900">{visit.patient_name}</p>
                <p className="text-xs text-gray-500">{visit.doctor_name || (isNursingOnlyVisit(visit) ? (visit.visit_type === 'PH' ? 'Pharmacy' : 'Nursing Only') : 'No doctor assigned')}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500">{new Date(visit.created_at).toLocaleDateString()} {new Date(visit.created_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</span>
              {/* Visit Type Badge */}
              <span className={`px-2 py-0.5 rounded text-xs font-medium ${getVisitTypeBadge(visit.visit_type).style}`}>
                {visit.visit_type || 'FU'}
              </span>
              <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${getStatusBadgeStyle(visit.status)}`}>
                {visit.status === 'pending_billing' ? 'Ready for Billing' : visit.status.replace(/_/g, ' ')}
              </span>
            </div>
          </div>
        </AccordionTrigger>
        <AccordionContent>
          <div className="pt-2 pb-4 border-t">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
              <div className="flex items-center gap-2 text-sm">
                <MapPin className="w-4 h-4 text-gray-400" />
                <span className="text-gray-600">Location:</span>
                <span className="text-gray-900">{visit.location_name || 'N/A'}</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Stethoscope className="w-4 h-4 text-gray-400" />
                <span className="text-gray-600">Services:</span>
                <span className="text-gray-900">{visit.services?.length || 0}</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Pill className="w-4 h-4 text-gray-400" />
                <span className="text-gray-600">Medications:</span>
                <span className="text-gray-900">{visit.medications?.length || 0}</span>
              </div>
              
              {/* Reassign Doctor - Front Desk / Admin only for checked_in and in_progress visits (not for Nursing Only) */}
              {(isFrontDesk() || isClinicAdmin()) && (visit.status === 'checked_in' || visit.status === 'in_progress') && !isNursingOnlyVisit(visit) && (
                <div className="flex items-center gap-2 text-sm">
                  <UserCog className="w-4 h-4 text-gray-400" />
                  <span className="text-gray-600">Doctor:</span>
                  <Select 
                    value={visit.doctor_id || ''} 
                    onValueChange={(value) => handleReassignDoctor(visit.id, value)}
                  >
                    <SelectTrigger className="h-8 w-44 text-xs" data-testid={`reassign-doctor-${visit.id}`}>
                      <SelectValue placeholder="Assign doctor" />
                    </SelectTrigger>
                    <SelectContent>
                      {doctors.map((doc) => (
                        <SelectItem key={doc.id} value={doc.id} className="text-xs">
                          {doc.full_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              
              {/* Nursing Only indicator */}
              {isNursingOnlyVisit(visit) && (
                <div className="flex items-center gap-2 text-sm">
                  <svg className="w-4 h-4 text-rose-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                  <span className="text-rose-600 font-medium">No billing required</span>
                </div>
              )}
            </div>

            {/* Estimated Total for pending_billing */}
            {isPendingBilling && (
              <div className="mt-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700">Estimated Total:</span>
                  <span className="text-lg font-semibold text-gray-900">
                    ${((visit.services?.reduce((sum, s) => sum + (s.total || 0), 0) || 0) + 
                       (visit.medications?.reduce((sum, m) => sum + (m.total || 0), 0) || 0)).toFixed(2)}
                  </span>
                </div>
              </div>
            )}

            {/* Bill History for Completed Visits */}
            {visit.status === 'completed' && (() => {
              const bill = getBillForVisit(visit.id);
              if (!bill && !isNursingOnlyVisit(visit)) return null;
              
              return (
                <div className="mt-4 space-y-3">
                  {/* Bill Information */}
                  {bill && (
                    <div className="p-4 bg-gradient-to-r from-teal-50 to-emerald-50 rounded-lg border border-teal-200">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-teal-600 flex items-center justify-center">
                            <Receipt className="w-5 h-5 text-white" />
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-teal-800">Associated Bill</p>
                            <p className="text-lg font-bold text-gray-900 font-mono">B{bill.id?.slice(-8).toUpperCase()}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-xl font-bold text-gray-900">${(bill.total || 0).toFixed(2)}</p>
                          <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                            bill.status === 'paid' ? 'bg-teal-100 text-teal-700' : 
                            bill.status === 'partially_paid' ? 'bg-amber-100 text-amber-700' : 
                            'bg-gray-100 text-gray-600'
                          }`}>
                            {bill.status?.replace(/_/g, ' ')}
                          </span>
                        </div>
                      </div>
                      
                      {/* Bill Summary */}
                      <div className="grid grid-cols-3 gap-4 pt-3 border-t border-teal-200">
                        <div className="text-sm">
                          <span className="text-teal-600">Services:</span>
                          <span className="ml-2 font-medium text-gray-900">{bill.services?.length || 0}</span>
                        </div>
                        <div className="text-sm">
                          <span className="text-teal-600">Medications:</span>
                          <span className="ml-2 font-medium text-gray-900">{bill.medications?.length || 0}</span>
                        </div>
                        <div className="text-sm">
                          <span className="text-teal-600">Paid:</span>
                          <span className="ml-2 font-medium text-gray-900">${(bill.amount_paid || 0).toFixed(2)}</span>
                        </div>
                      </div>
                      
                      {/* View Bill Button */}
                      <div className="mt-3 pt-3 border-t border-teal-200">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => navigate(`/billing?tab=${bill.status === 'paid' ? 'paid' : bill.status === 'partially_paid' ? 'partially_paid' : 'pending'}&bill=${bill.id}`)}
                          className="text-teal-700 border-teal-300 hover:bg-teal-100"
                          data-testid={`view-bill-completed-${visit.id}`}
                        >
                          <Receipt className="w-4 h-4 mr-1" />
                          View Full Bill
                        </Button>
                      </div>
                    </div>
                  )}
                  
                  {/* Doctor's Notes from Bill */}
                  {bill?.visit_notes && (
                    <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                      <div className="flex items-center gap-2 mb-2">
                        <ClipboardList className="w-4 h-4 text-gray-500" />
                        <span className="text-sm font-semibold text-gray-700">Doctor's Notes</span>
                      </div>
                      <p className="text-sm text-gray-600 whitespace-pre-wrap">{bill.visit_notes}</p>
                    </div>
                  )}
                  
                  {/* Visit Notes (if different from bill notes) */}
                  {visit.notes && visit.notes !== bill?.visit_notes && (
                    <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                      <div className="flex items-center gap-2 mb-2">
                        <FileText className="w-4 h-4 text-gray-500" />
                        <span className="text-sm font-semibold text-gray-700">Visit Notes</span>
                      </div>
                      <p className="text-sm text-gray-600 whitespace-pre-wrap">{visit.notes}</p>
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Actions */}
            <div className="flex items-center gap-2 mt-4 pt-4 border-t">
              {/* Doctor/Admin can manage if not completed */}
              {canManageTreatment(visit) && visit.status !== 'completed' && visit.status !== 'pending_billing' && !isNursingOnlyVisit(visit) && (
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => openTreatmentDialog(visit)}
                  className="text-teal-700 border-teal-200 hover:bg-teal-50 bg-teal-50"
                  data-testid={`manage-visit-${visit.id}`}
                >
                  <Edit className="w-4 h-4 mr-1" />
                  Manage Patient
                </Button>
              )}
              
              {/* Cancel Visit - Front Desk / Admin only, checked_in only */}
              {(isFrontDesk() || isClinicAdmin()) && visit.status === 'checked_in' && (
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => openCancelDialog(visit)}
                  className="text-red-600 border-red-200 hover:bg-red-50"
                  data-testid={`cancel-visit-${visit.id}`}
                >
                  <XCircle className="w-4 h-4 mr-1" />
                  Cancel Visit
                </Button>
              )}

              
              {/* Close Visit button for Nursing Only visits - Front Desk/Admin only */}
              {isNursingOnlyVisit(visit) && (isFrontDesk() || isClinicAdmin()) && visit.status !== 'completed' && (
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => closeVisitWithoutBilling(visit)}
                  className="text-rose-700 border-rose-200 hover:bg-rose-50 bg-rose-50"
                  data-testid={`close-visit-${visit.id}`}
                >
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Close Visit
                </Button>
              )}
              
              {/* Front Desk sees Bill Number for pending_billing visits */}
              {isPendingBilling && (isFrontDesk() || isClinicAdmin()) && (() => {
                const bill = getBillForVisit(visit.id);
                return bill ? (
                  <div 
                    onClick={() => navigate(`/billing?tab=${bill.status === 'paid' ? 'paid' : bill.status === 'partially_paid' ? 'partially_paid' : 'pending'}&bill=${bill.id}`)}
                    className="flex items-center gap-3 px-4 py-3 bg-gradient-to-r from-slate-50 to-slate-100 hover:from-slate-100 hover:to-slate-150 rounded-xl cursor-pointer transition-all group border border-gray-200"
                    data-testid={`view-bill-${visit.id}`}
                  >
                    <div className="w-10 h-10 rounded-lg bg-emerald-500 flex items-center justify-center">
                      <Receipt className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="text-lg font-bold text-gray-900 font-mono tracking-wide">
                        B{bill.id?.slice(-8).toUpperCase()}
                      </p>
                      <p className="text-xs text-gray-500">Click to view bill</p>
                    </div>
                  </div>
                ) : (
                  <span className="text-sm text-gray-500">No bill created</span>
                );
              })()}
            </div>
          </div>
        </AccordionContent>
      </AccordionItem>
    );
  };

  // ==================== DOCTOR VIEW - iPad App Style ====================
  
  // Bill edit functions for doctor
  const openBillEditDialog = (bill) => {
    setSelectedBill(bill);
    setBillEditForm({
      services: bill.services || [],
      medications: bill.medications || [],
      notes: bill.visit_notes || ''
    });
    setServiceSearch('');
    setMedicationSearch('');
    setServiceDropdownOpen(false);
    setMedicationDropdownOpen(false);
    setRevisionReason('');
    setActiveBillEditTab('services');
    // Use full-page view instead of dialog
    setDoctorViewTab('editBill');
  };

  const closeBillEditView = () => {
    setDoctorViewTab('history');
    setSelectedBill(null);
    setServiceSearch('');
    setMedicationSearch('');
    setServiceDropdownOpen(false);
    setMedicationDropdownOpen(false);
    setRevisionReason('');
    setShowRevisionReasonDialog(false);
  };

  const addServiceToBill = (serviceId) => {
    // Look up from searched results first, then fall back to services list
    const service = searchedServices.find(s => s.id === serviceId) || services.find(s => s.id === serviceId);
    if (!service || billEditForm.services.some(s => s.service_id === serviceId)) return;
    
    const price = service.price || 0;
    setBillEditForm({
      ...billEditForm,
      services: [...billEditForm.services, {
        service_id: service.id,
        service_name: service.name,
        quantity: 1,
        unit_price: price,
        discount: 0,
        total: price,
        notes: ''
      }]
    });
    setServiceSearch('');
    setServiceDropdownOpen(false);
  };

  const updateBillServiceQuantity = (index, quantity) => {
    const updated = [...billEditForm.services];
    updated[index].quantity = parseInt(quantity) || 1;
    const subtotal = updated[index].quantity * updated[index].unit_price;
    updated[index].total = subtotal - (updated[index].discount || 0);
    setBillEditForm({ ...billEditForm, services: updated });
  };

  const updateBillServicePrice = (index, price) => {
    const updated = [...billEditForm.services];
    // Remove leading zeros and parse the price
    const cleanPrice = typeof price === 'string' ? price.replace(/^0+(?=\d)/, '') : price;
    updated[index].unit_price = parseFloat(cleanPrice) || 0;
    const subtotal = updated[index].quantity * updated[index].unit_price;
    updated[index].total = subtotal - (updated[index].discount || 0);
    setBillEditForm({ ...billEditForm, services: updated });
  };

  const updateBillServiceDiscount = (index, discount) => {
    const updated = [...billEditForm.services];
    const discountValue = parseFloat(discount) || 0;
    const subtotal = updated[index].quantity * updated[index].unit_price;
    updated[index].discount = Math.min(discountValue, subtotal);
    updated[index].total = subtotal - updated[index].discount;
    setBillEditForm({ ...billEditForm, services: updated });
  };

  const removeBillService = (index) => {
    setBillEditForm({
      ...billEditForm,
      services: billEditForm.services.filter((_, i) => i !== index)
    });
  };

  const addMedicationToBill = (inventoryId) => {
    const item = searchedMedications.find(i => i.id === inventoryId);
    if (!item || billEditForm.medications.some(m => m.inventory_id === inventoryId)) return;
    
    const stock = item.quantity || item.quantity_on_hand || 0;
    if (stock < 1) {
      toast.error(`${item.name} is out of stock`);
      return;
    }
    
    const price = item.unit_price || 0;
    setBillEditForm({
      ...billEditForm,
      medications: [...billEditForm.medications, {
        inventory_id: item.id,
        medication_name: item.name,
        quantity: 1,
        unit_price: price,
        total: price,
        notes: '',
        available_stock: stock // Store available stock for validation
      }]
    });
    setMedicationSearch('');
    setSearchedMedications([]); // Clear search results
    setMedicationDropdownOpen(false);
  };

  const updateBillMedicationQuantity = (index, quantity) => {
    const updated = [...billEditForm.medications];
    updated[index].quantity = parseInt(quantity) || 1;
    updated[index].total = updated[index].quantity * updated[index].unit_price;
    setBillEditForm({ ...billEditForm, medications: updated });
  };

  const updateBillMedicationPrice = (index, price) => {
    const updated = [...billEditForm.medications];
    // Remove leading zeros and parse the price
    const cleanPrice = typeof price === 'string' ? price.replace(/^0+(?=\d)/, '') : price;
    updated[index].unit_price = parseFloat(cleanPrice) || 0;
    updated[index].total = updated[index].quantity * updated[index].unit_price;
    setBillEditForm({ ...billEditForm, medications: updated });
  };

  const removeBillMedication = (index) => {
    setBillEditForm({
      ...billEditForm,
      medications: billEditForm.medications.filter((_, i) => i !== index)
    });
  };

  const handleSaveBillRevision = async () => {
    if (!selectedBill) return;
    
    // Validate revision reason is provided
    if (!revisionReason.trim()) {
      toast.error('Please provide a reason for the revision');
      return;
    }
    
    try {
      await billsAPI.doctorRevise(selectedBill.id, {
        services: billEditForm.services,
        medications: billEditForm.medications,
        notes: billEditForm.notes,
        revision_reason: revisionReason.trim()
      });
      toast.success('Bill updated - Front Desk will see the changes');
      // Navigate back to bill history
      closeBillEditView();
      refreshAllTabs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update bill');
    }
  };

  // Bill Edit Dialog for doctors
  // Note: Bill edit now uses full-page view (doctorViewTab === 'editBill')
  // The dialog is kept as empty for backwards compatibility
  const renderBillEditDialog = () => null;
  
  if (isDoctor()) {
    // Editable bills (already filtered to pending/partially_paid in fetchDoctorHistory)
    const pendingDoctorBills = doctorBills;

    // Use server-side searched services (no client-side filtering needed)
    const filteredServicesManage = searchedServices;
    // Use server-side searched medications (no client-side filtering needed)
    const filteredMedicationsManage = searchedMedications;

    // Use server-side searched services for bill edit view too
    const filteredServicesForBill = searchedServices;
    // Use server-side searched medications for bill edit view too
    const filteredMedicationsForBill = searchedMedications;

    const treatmentTotal = treatmentForm.services.reduce((sum, s) => sum + (s.total || 0), 0) + 
                          treatmentForm.medications.reduce((sum, m) => sum + (m.total || 0), 0);

    const billTotal = billEditForm.services.reduce((sum, s) => sum + (s.total || 0), 0) + 
                      billEditForm.medications.reduce((sum, m) => sum + (m.total || 0), 0);

    const billTotalDiscount = billEditForm.services.reduce((sum, s) => sum + (s.discount || 0), 0) + 
                              billEditForm.medications.reduce((sum, m) => sum + (m.discount || 0), 0);

    // ==================== FULL PAGE EDIT BILL VIEW ====================
    if (doctorViewTab === 'editBill' && selectedBill) {
      return (
        <div className="min-h-screen bg-gray-50 -m-6" data-testid="edit-bill-page">
          {/* Fixed Header */}
          <div className="fixed top-0 left-0 right-0 z-30 bg-white border-b border-gray-200 shadow-sm">
            {/* Revision Mode Banner */}
            <div className="bg-gradient-to-r from-amber-500 to-orange-500 px-4 py-2">
              <div className="flex items-center justify-center gap-2 text-white">
                <RotateCcw className="w-4 h-4" />
                <span className="text-sm font-semibold">REVISION MODE</span>
                <span className="text-xs opacity-80">• Changes will be logged and visible to Front Desk</span>
              </div>
            </div>
            
            <div className="flex items-center justify-between p-4">
              <button 
                onClick={closeBillEditView}
                className="flex items-center gap-2 text-gray-600 hover:text-gray-900 active:scale-95 transition-all"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                <span className="text-base font-medium">Back</span>
              </button>
              <div className="text-center">
                <p className="font-bold text-gray-900">{selectedBill.patient_name}</p>
                <p className="text-xs text-gray-500">Bill #{selectedBill.id?.slice(-8).toUpperCase()}</p>
              </div>
              <div className="text-right">
                <p className="text-xl font-bold text-gray-900">${billTotal.toFixed(2)}</p>
                {billTotalDiscount > 0 && (
                  <p className="text-xs text-blue-600">-${billTotalDiscount.toFixed(2)} discount</p>
                )}
              </div>
            </div>
            
            {/* Tab Navigation */}
            <div className="px-4 pb-3">
              <div className="flex gap-2">
                <button
                  onClick={() => { setActiveBillEditTab('services'); setServiceDropdownOpen(false); setMedicationDropdownOpen(false); }}
                  className={`flex-1 py-3 px-4 rounded-xl text-sm font-semibold transition-all active:scale-95 ${
                    activeBillEditTab === 'services'
                      ? 'bg-gradient-to-r from-teal-600 to-blue-600 text-white shadow-md'
                      : 'bg-gray-100 text-gray-600'
                  }`}
                >
                  <Stethoscope className="w-5 h-5 inline mr-2" />
                  Services
                  {billEditForm.services.length > 0 && (
                    <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
                      activeBillEditTab === 'services' ? 'bg-white/20' : 'bg-blue-100 text-blue-700'
                    }`}>
                      {billEditForm.services.length}
                    </span>
                  )}
                </button>
                <button
                  onClick={() => { setActiveBillEditTab('medications'); setServiceDropdownOpen(false); setMedicationDropdownOpen(false); }}
                  className={`flex-1 py-3 px-4 rounded-xl text-sm font-semibold transition-all active:scale-95 ${
                    activeBillEditTab === 'medications'
                      ? 'bg-gradient-to-r from-teal-600 to-blue-600 text-white shadow-md'
                      : 'bg-gray-100 text-gray-600'
                  }`}
                >
                  <Pill className="w-5 h-5 inline mr-2" />
                  Meds
                  {billEditForm.medications.length > 0 && (
                    <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
                      activeBillEditTab === 'medications' ? 'bg-white/20' : 'bg-blue-100 text-blue-700'
                    }`}>
                      {billEditForm.medications.length}
                    </span>
                  )}
                </button>
                <button
                  onClick={() => { setActiveBillEditTab('notes'); setServiceDropdownOpen(false); setMedicationDropdownOpen(false); }}
                  className={`flex-1 py-3 px-4 rounded-xl text-sm font-semibold transition-all active:scale-95 ${
                    activeBillEditTab === 'notes'
                      ? 'bg-gradient-to-r from-teal-600 to-blue-600 text-white shadow-md'
                      : 'bg-gray-100 text-gray-600'
                  }`}
                >
                  <ClipboardList className="w-5 h-5 inline mr-2" />
                  Notes
                </button>
              </div>
            </div>
          </div>

          {/* Content Area - extra top padding for revision banner */}
          <div className="pt-[180px] px-3 pb-32">
            {/* SERVICES TAB */}
            {activeBillEditTab === 'services' && (
              <div className="space-y-3">
                <div className="relative">
                  <Input
                    placeholder="Type to search services..."
                    value={serviceSearch}
                    onChange={(e) => setServiceSearch(e.target.value)}
                    className="h-9 text-sm bg-gray-50 border-gray-200 rounded-md"
                  />
                  {serviceSearch && (
                    <button
                      onClick={() => { setServiceSearch(''); setSearchedServices([]); }}
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                  
                  {serviceSearch.length >= 2 && (
                    <div className="absolute left-0 right-0 mt-1 bg-white rounded-md border border-gray-200 shadow-lg max-h-48 overflow-y-auto z-50">
                      {servicesLoading ? (
                        <div className="p-3 text-center text-gray-500 text-sm">
                          <div className="animate-spin inline-block w-4 h-4 border-2 border-teal-500 border-t-transparent rounded-full mr-2"></div>
                          Searching...
                        </div>
                      ) : filteredServicesForBill.length === 0 ? (
                        <p className="p-3 text-center text-gray-400 text-sm">No services found</p>
                      ) : (
                        filteredServicesForBill.map((s) => (
                          <button
                            key={s.id}
                            onClick={() => addServiceToBill(s.id)}
                            className="w-full flex justify-between items-center p-2 text-sm hover:bg-gray-50 border-b border-gray-100 last:border-0"
                          >
                            <div className="flex items-center gap-2">
                              <span className="text-gray-900">{s.name}</span>
                              {s.category === 'Adjustable Procedure' && (
                                <span className="px-1.5 py-0.5 text-[10px] font-semibold bg-amber-100 text-amber-700 rounded uppercase tracking-wide">Custom Price</span>
                              )}
                            </div>
                            <span className="text-gray-600">${(s.price || 0).toFixed(2)}</span>
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </div>

                {billEditForm.services.length > 0 && (
                  <div className="space-y-2">
                    {billEditForm.services.map((s, idx) => (
                      <div key={idx} className="bg-white rounded-md p-3 border border-gray-200 flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-medium text-gray-900">{s.service_name}</p>
                            {s.is_adjustable && (
                              <span className="px-1.5 py-0.5 text-[10px] font-semibold bg-amber-100 text-amber-700 rounded uppercase tracking-wide">Custom Price</span>
                            )}
                          </div>
                          {/* Editable price for revision */}
                          <div className="flex items-center gap-2 mt-1.5">
                            <div className="relative flex items-center">
                              <span className={`absolute left-2 font-medium text-sm ${s.is_adjustable ? 'text-amber-600' : 'text-teal-600'}`}>$</span>
                              <input
                                type="text"
                                inputMode="decimal"
                                value={s.unit_price || ''}
                                onChange={(e) => {
                                  const val = e.target.value.replace(/[^0-9.]/g, '');
                                  updateBillServicePrice(idx, val);
                                }}
                                className={`w-24 h-8 pl-5 pr-2 text-sm font-semibold border-2 rounded-lg focus:outline-none focus:ring-2 transition-all ${
                                  s.is_adjustable 
                                    ? 'text-amber-800 border-amber-300 bg-amber-50 focus:ring-amber-400 focus:border-amber-400' 
                                    : 'text-teal-800 border-teal-300 bg-teal-50 focus:ring-teal-400 focus:border-teal-400'
                                }`}
                              />
                            </div>
                            <span className="text-xs text-gray-500">per unit</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="flex items-center bg-gray-100 rounded">
                            <button onClick={() => updateBillServiceQuantity(idx, Math.max(1, s.quantity - 1))} className="w-7 h-7 text-gray-600 font-bold">-</button>
                            <span className="w-6 text-center text-sm font-medium">{s.quantity}</span>
                            <button onClick={() => updateBillServiceQuantity(idx, s.quantity + 1)} className="w-7 h-7 text-gray-600 font-bold">+</button>
                          </div>
                          <span className="text-sm font-semibold text-gray-800 w-16 text-right">${(s.total || 0).toFixed(2)}</span>
                          <button
                            onClick={() => removeBillService(idx)}
                            className="text-gray-400 hover:text-red-500 p-1"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {billEditForm.services.length === 0 && (
                  <div className="bg-white rounded-md p-6 text-center border border-dashed border-gray-300 mt-2">
                    <Stethoscope className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                    <p className="text-sm text-gray-500">No services added</p>
                    <p className="text-xs text-gray-400 mt-1">Type above to search and add services</p>
                  </div>
                )}
              </div>
            )}

            {/* MEDICATIONS TAB */}
            {activeBillEditTab === 'medications' && (
              <div className="space-y-3">
                <div className="relative">
                  <Input
                    placeholder="Type to search medications (min 2 chars)..."
                    value={medicationSearch}
                    onChange={(e) => setMedicationSearch(e.target.value)}
                    className="h-9 text-sm bg-gray-50 border-gray-200 rounded-md"
                  />
                  {medicationSearch && (
                    <button
                      onClick={() => { setMedicationSearch(''); setSearchedMedications([]); }}
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                  
                  {medicationSearch.length >= 2 && (
                    <div className="absolute left-0 right-0 mt-1 bg-white rounded-md border border-gray-200 shadow-lg max-h-48 overflow-y-auto z-50">
                      {medicationsLoading ? (
                        <div className="p-3 text-center text-gray-500 text-sm">
                          <div className="animate-spin inline-block w-4 h-4 border-2 border-teal-500 border-t-transparent rounded-full mr-2"></div>
                          Searching...
                        </div>
                      ) : filteredMedicationsForBill.length === 0 ? (
                        <p className="p-3 text-center text-gray-400 text-sm">No medications found</p>
                      ) : (
                        filteredMedicationsForBill.map((i) => {
                          const stock = i.quantity_on_hand || i.quantity || 0;
                          const isOutOfStock = stock === 0;
                          const isLowStock = stock > 0 && stock <= (i.reorder_threshold || 10);
                          return (
                            <button
                              key={i.id}
                              onClick={() => !isOutOfStock && addMedicationToBill(i.id)}
                              disabled={isOutOfStock}
                              className={`w-full flex justify-between items-center p-2 text-sm border-b border-gray-100 last:border-0 ${
                                isOutOfStock 
                                  ? 'bg-gray-50 cursor-not-allowed opacity-70' 
                                  : 'hover:bg-gray-50 cursor-pointer'
                              }`}
                            >
                              <div className="flex items-center gap-2">
                                <span className={isOutOfStock ? 'text-gray-500' : 'text-gray-900'}>{i.name}</span>
                                <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${
                                  isOutOfStock 
                                    ? 'bg-red-100 text-red-700' 
                                    : isLowStock 
                                      ? 'bg-amber-100 text-amber-700' 
                                      : 'bg-emerald-100 text-emerald-700'
                                }`}>
                                  {isOutOfStock ? 'Out of Stock' : `${stock} in stock`}
                                </span>
                              </div>
                              <span className={isOutOfStock ? 'text-gray-400' : 'text-gray-600'}>${(i.unit_price || 0).toFixed(2)}</span>
                            </button>
                          );
                        })
                      )}
                    </div>
                  )}
                </div>

                {billEditForm.medications.length > 0 && (
                  <div className="space-y-2">
                    {billEditForm.medications.map((m, idx) => (
                      <div key={idx} className="bg-white rounded-md p-3 border border-gray-200 flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900">{m.medication_name}</p>
                          {/* Editable price for revision */}
                          <div className="flex items-center gap-2 mt-1.5">
                            <div className="relative flex items-center">
                              <span className="absolute left-2 font-medium text-sm text-teal-600">$</span>
                              <input
                                type="text"
                                inputMode="decimal"
                                value={m.unit_price || ''}
                                onChange={(e) => {
                                  const val = e.target.value.replace(/[^0-9.]/g, '');
                                  updateBillMedicationPrice(idx, val);
                                }}
                                className="w-24 h-8 pl-5 pr-2 text-sm font-semibold text-teal-800 border-2 border-teal-300 bg-teal-50 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-teal-400 transition-all"
                              />
                            </div>
                            <span className="text-xs text-gray-500">per unit</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="flex items-center bg-gray-100 rounded">
                            <button onClick={() => updateBillMedicationQuantity(idx, Math.max(1, m.quantity - 1))} className="w-7 h-7 text-gray-600 font-bold">-</button>
                            <span className="w-6 text-center text-sm font-medium">{m.quantity}</span>
                            <button onClick={() => updateBillMedicationQuantity(idx, m.quantity + 1)} className="w-7 h-7 text-gray-600 font-bold">+</button>
                          </div>
                          <span className="text-sm font-semibold text-gray-800 w-16 text-right">${(m.total || 0).toFixed(2)}</span>
                          <button
                            onClick={() => removeBillMedication(idx)}
                            className="text-gray-400 hover:text-red-500 p-1"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {billEditForm.medications.length === 0 && (
                  <div className="bg-white rounded-md p-6 text-center border border-dashed border-gray-300 mt-2">
                    <Pill className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                    <p className="text-sm text-gray-500">No medications added</p>
                    <p className="text-xs text-gray-400 mt-1">Type above to search and add medications</p>
                  </div>
                )}
              </div>
            )}

            {/* NOTES TAB */}
            {activeBillEditTab === 'notes' && (
              <div className="space-y-3">
                <div>
                  <Label className="text-sm font-medium text-gray-700 mb-2 block">Clinical Notes</Label>
                  <Textarea
                    value={billEditForm.notes}
                    onChange={(e) => setBillEditForm({ ...billEditForm, notes: e.target.value })}
                    placeholder="Add any clinical notes for this visit..."
                    rows={6}
                    className="text-sm bg-white border-gray-200 rounded-md resize-none"
                  />
                  <p className="text-xs text-gray-400 mt-1">These notes will be saved with the bill</p>
                </div>
              </div>
            )}
          </div>

          {/* Fixed Bottom Action Bar */}
          <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 shadow-lg z-30">
            <div className="flex items-center justify-between mb-3 text-sm">
              <div className="flex gap-4 flex-wrap">
                <span className="text-gray-500">
                  Services: <span className="font-semibold text-gray-900">${billEditForm.services.reduce((sum, s) => sum + (s.total || 0), 0).toFixed(2)}</span>
                </span>
                <span className="text-gray-500">
                  Meds: <span className="font-semibold text-gray-900">${billEditForm.medications.reduce((sum, m) => sum + (m.total || 0), 0).toFixed(2)}</span>
                </span>
                {billTotalDiscount > 0 && (
                  <span className="text-gray-500">
                    Discount: <span className="font-semibold">-${billTotalDiscount.toFixed(2)}</span>
                  </span>
                )}
              </div>
              <span className="text-xl font-bold text-gray-900">${billTotal.toFixed(2)}</span>
            </div>
            
            <div className="flex gap-3">
              <Button 
                variant="outline" 
                onClick={closeBillEditView} 
                className="flex-1 h-14 text-base rounded-2xl border-2 border-gray-300 active:scale-95 transition-transform"
              >
                Cancel
              </Button>
              <Button 
                onClick={() => setShowRevisionReasonDialog(true)} 
                className="flex-1 h-14 text-base bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white rounded-2xl active:scale-95 transition-transform shadow-md"
                data-testid="save-bill-revision-btn"
              >
                <RotateCcw className="w-5 h-5 mr-2" />
                Submit Revision
              </Button>
            </div>
          </div>
          
          {/* Revision Reason Dialog */}
          {showRevisionReasonDialog && (
            <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
              <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden">
                <div className="bg-gradient-to-r from-amber-500 to-orange-500 p-4">
                  <h3 className="text-lg font-bold text-white flex items-center gap-2">
                    <RotateCcw className="w-5 h-5" />
                    Revision Reason Required
                  </h3>
                  <p className="text-sm text-white/80 mt-1">Please explain why you're revising this bill</p>
                </div>
                
                <div className="p-6">
                  <Textarea
                    value={revisionReason}
                    onChange={(e) => setRevisionReason(e.target.value)}
                    placeholder="Example: Added additional service performed during visit, corrected medication dosage, removed duplicate entry..."
                    rows={4}
                    className={`w-full text-base bg-gray-50 border-2 rounded-xl resize-none ${
                      revisionReason.trim() ? 'border-gray-200 focus:border-amber-500' : 'border-red-300 focus:border-red-500'
                    }`}
                    autoFocus
                  />
                  <p className="text-xs text-gray-500 mt-2">
                    This reason will be logged and visible to Front Desk staff for audit purposes.
                  </p>
                </div>
                
                <div className="flex gap-3 p-4 bg-gray-50 border-t border-gray-100">
                  <Button 
                    variant="outline" 
                    onClick={() => {
                      setShowRevisionReasonDialog(false);
                      setRevisionReason('');
                    }}
                    className="flex-1 h-12 rounded-xl"
                  >
                    Cancel
                  </Button>
                  <Button 
                    onClick={() => {
                      if (revisionReason.trim()) {
                        handleSaveBillRevision();
                        setShowRevisionReasonDialog(false);
                      }
                    }}
                    disabled={!revisionReason.trim()}
                    className="flex-1 h-12 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white rounded-xl disabled:opacity-50"
                  >
                    Submit Revision
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      );
    }

    // ==================== FULL PAGE MANAGE PATIENT VIEW ====================
    if (doctorViewTab === 'manage' && selectedVisit) {
      return (
        <div className="min-h-screen bg-gray-50 -m-6" data-testid="manage-patient-page">
          {/* Fixed Header with Tabs and Search */}
          <div className="fixed top-0 left-0 right-0 z-30 bg-white border-b border-gray-200">
            {/* Top Bar */}
            <div className="flex items-center justify-between px-4 py-2">
              <button 
                onClick={closeManagePatientView}
                className="flex items-center gap-1 text-gray-600 hover:text-gray-900"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                <span className="text-sm">Back</span>
              </button>
              <p className="font-semibold text-gray-900 text-sm">{selectedVisit.patient_name}</p>
              <p className="text-sm font-semibold text-gray-800">${treatmentTotal.toFixed(2)}</p>
            </div>
            
            {/* Tabs */}
            <div className="flex gap-1 px-3 pb-2">
              <button
                onClick={() => { setActiveManageTab('services'); setServiceDropdownOpen(false); setMedicationDropdownOpen(false); }}
                className={`flex-1 py-2 rounded-md text-sm font-medium ${
                  activeManageTab === 'services' ? 'bg-gradient-to-r from-teal-600 to-blue-600 text-white' : 'bg-gray-100 text-gray-600'
                }`}
              >
                Services {treatmentForm.services.length > 0 && `(${treatmentForm.services.length})`}
              </button>
              <button
                onClick={() => { setActiveManageTab('medications'); setServiceDropdownOpen(false); setMedicationDropdownOpen(false); }}
                className={`flex-1 py-2 rounded-md text-sm font-medium ${
                  activeManageTab === 'medications' ? 'bg-gradient-to-r from-teal-600 to-blue-600 text-white' : 'bg-gray-100 text-gray-600'
                }`}
              >
                Meds {treatmentForm.medications.length > 0 && `(${treatmentForm.medications.length})`}
              </button>
              <button
                onClick={() => { setActiveManageTab('notes'); setServiceDropdownOpen(false); setMedicationDropdownOpen(false); }}
                className={`flex-1 py-2 rounded-md text-sm font-medium ${
                  activeManageTab === 'notes' ? 'bg-gradient-to-r from-teal-600 to-blue-600 text-white' : 'bg-gray-100 text-gray-600'
                }`}
              >
                Notes
              </button>
            </div>

            {/* Search - Services */}
            {activeManageTab === 'services' && (
              <div className="px-3 pb-2 relative">
                <Input
                  placeholder="Type to search services..."
                  value={serviceSearch}
                  onChange={(e) => setServiceSearch(e.target.value)}
                  className="h-9 text-sm bg-gray-50 border-gray-200 rounded-md"
                />
                {serviceSearch.length >= 2 && (
                  <div className="absolute left-3 right-3 mt-1 bg-white rounded-md border border-gray-200 shadow-lg max-h-48 overflow-y-auto z-50">
                    {servicesLoading ? (
                      <div className="p-3 text-center text-gray-500 text-sm">
                        <div className="animate-spin inline-block w-4 h-4 border-2 border-teal-500 border-t-transparent rounded-full mr-2"></div>
                        Searching...
                      </div>
                    ) : filteredServicesManage.length > 0 ? (
                      filteredServicesManage.map((s) => (
                        <button
                          key={s.id}
                          onClick={() => addService(s.id)}
                          className="w-full flex justify-between items-center p-2 text-sm hover:bg-gray-50 border-b border-gray-100 last:border-0"
                        >
                          <div className="flex items-center gap-2">
                            <span className="text-gray-900">{s.name}</span>
                            {s.category === 'Adjustable Procedure' && (
                              <span className="px-1.5 py-0.5 text-[10px] font-semibold bg-amber-100 text-amber-700 rounded uppercase tracking-wide">Custom Price</span>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            {s.category === 'Adjustable Procedure' && (
                              <svg className="w-3.5 h-3.5 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                              </svg>
                            )}
                            <span className="text-gray-600">${(s.price || 0).toFixed(2)}</span>
                          </div>
                        </button>
                      ))
                    ) : (
                      <p className="p-3 text-center text-gray-400 text-sm">No services found</p>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Search - Medications */}
            {activeManageTab === 'medications' && (
              <div className="px-3 pb-2 relative">
                <Input
                  placeholder="Type to search medications (min 2 chars)..."
                  value={medicationSearch}
                  onChange={(e) => setMedicationSearch(e.target.value)}
                  className="h-9 text-sm bg-gray-50 border-gray-200 rounded-md"
                />
                {medicationSearch.length >= 2 && (
                  <div className="absolute left-3 right-3 mt-1 bg-white rounded-md border border-gray-200 shadow-lg max-h-48 overflow-y-auto z-50">
                    {medicationsLoading ? (
                      <div className="p-3 text-center text-gray-500 text-sm">
                        <div className="animate-spin inline-block w-4 h-4 border-2 border-teal-500 border-t-transparent rounded-full mr-2"></div>
                        Searching...
                      </div>
                    ) : filteredMedicationsManage.length > 0 ? (
                      filteredMedicationsManage.map((i) => {
                        const stock = i.quantity_on_hand || i.quantity || 0;
                        const isOutOfStock = stock === 0;
                        const isLowStock = stock > 0 && stock <= (i.reorder_threshold || 10);
                        return (
                          <button
                            key={i.id}
                            onClick={() => !isOutOfStock && addMedication(i.id)}
                            disabled={isOutOfStock}
                            className={`w-full flex justify-between items-center p-2 text-sm border-b border-gray-100 last:border-0 ${
                              isOutOfStock 
                                ? 'bg-gray-50 cursor-not-allowed opacity-70' 
                                : 'hover:bg-gray-50 cursor-pointer'
                            }`}
                          >
                            <div className="flex items-center gap-2">
                              <span className={isOutOfStock ? 'text-gray-500' : 'text-gray-900'}>{i.name}</span>
                              <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${
                                isOutOfStock 
                                  ? 'bg-red-100 text-red-700' 
                                  : isLowStock 
                                    ? 'bg-amber-100 text-amber-700' 
                                    : 'bg-emerald-100 text-emerald-700'
                              }`}>
                                {isOutOfStock ? 'Out of Stock' : `${stock} in stock`}
                              </span>
                            </div>
                            <span className={isOutOfStock ? 'text-gray-400' : 'text-gray-600'}>${(i.unit_price || 0).toFixed(2)}</span>
                          </button>
                        );
                      })
                    ) : (
                      <div className="p-3 text-center text-gray-500 text-sm">
                        No medications found for "{medicationSearch}"
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Scrollable Content */}
          <div className="px-3 pb-24 overflow-y-auto" style={{ paddingTop: activeManageTab === 'notes' ? '95px' : '150px' }}>
            {/* Services List */}
            {activeManageTab === 'services' && (
              <div className="space-y-2">
                {treatmentForm.services.map((s, idx) => (
                  <div key={idx} className="bg-white rounded-md p-3 border border-gray-200 flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium text-gray-900">{s.service_name}</p>
                        {s.is_adjustable && (
                          <span className="px-1.5 py-0.5 text-[10px] font-semibold bg-amber-100 text-amber-700 rounded uppercase tracking-wide">Custom Price</span>
                        )}
                      </div>
                      {/* Editable prices for doctors and admins only */}
                      {canEditPrices ? (
                        <div className="flex items-center gap-2 mt-1.5">
                          <div className="relative flex items-center">
                            <span className={`absolute left-2 font-medium text-sm ${s.is_adjustable ? 'text-amber-600' : 'text-teal-600'}`}>$</span>
                            <input
                              type="text"
                              inputMode="decimal"
                              value={s.unit_price || ''}
                              onChange={(e) => {
                                const val = e.target.value.replace(/[^0-9.]/g, '');
                                updateServicePrice(idx, val);
                              }}
                              className={`w-24 h-8 pl-5 pr-2 text-sm font-semibold border-2 rounded-lg focus:outline-none focus:ring-2 transition-all ${
                                s.is_adjustable 
                                  ? 'text-amber-800 border-amber-300 bg-amber-50 focus:ring-amber-400 focus:border-amber-400' 
                                  : 'text-teal-800 border-teal-300 bg-teal-50 focus:ring-teal-400 focus:border-teal-400'
                              }`}
                            />
                          </div>
                          <span className="text-xs text-gray-500">per unit</span>
                        </div>
                      ) : (
                        <p className="text-xs text-gray-500 mt-0.5">${(s.unit_price || 0).toFixed(2)} each</p>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="flex items-center bg-gray-100 rounded">
                        <button onClick={() => updateServiceQuantity(idx, Math.max(1, s.quantity - 1))} className="w-7 h-7 text-gray-600 font-bold">-</button>
                        <span className="w-6 text-center text-sm font-medium">{s.quantity}</span>
                        <button onClick={() => updateServiceQuantity(idx, s.quantity + 1)} className="w-7 h-7 text-gray-600 font-bold">+</button>
                      </div>
                      <span className="text-sm font-semibold text-gray-800 w-16 text-right">${(s.total || 0).toFixed(2)}</span>
                      <button onClick={() => removeService(idx)} className="text-gray-400 hover:text-red-500 p-1">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  </div>
                ))}
                {treatmentForm.services.length === 0 && (
                  <p className="text-center text-gray-400 text-sm py-8">No services added</p>
                )}
              </div>
            )}

            {/* Medications List */}
            {activeManageTab === 'medications' && (
              <div className="space-y-2">
                {treatmentForm.medications.map((m, idx) => {
                  const availableStock = m.available_stock || 0;
                  return (
                    <div key={idx} className="bg-white rounded-md p-3 border border-gray-200 flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900">{m.medication_name}</p>
                        <div className="flex items-center gap-2 mt-1">
                          {canEditPrices ? (
                            <div className="relative flex items-center">
                              <span className="absolute left-2 text-teal-600 font-medium text-xs">$</span>
                              <input
                                type="text"
                                inputMode="decimal"
                                value={m.unit_price || ''}
                                onChange={(e) => {
                                  const val = e.target.value.replace(/[^0-9.]/g, '');
                                  updateMedicationPrice(idx, val);
                                }}
                                className="w-20 h-7 pl-4 pr-2 text-xs text-teal-800 font-semibold border border-teal-300 rounded bg-teal-50 focus:outline-none focus:ring-1 focus:ring-teal-400 focus:border-teal-400 transition-all"
                              />
                            </div>
                          ) : (
                            <span className="text-xs text-gray-600 font-medium">${(m.unit_price || 0).toFixed(2)}</span>
                          )}
                          <span className="text-xs px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700">
                            {availableStock} available
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="flex items-center bg-gray-100 rounded">
                          <button onClick={() => updateMedicationQuantity(idx, Math.max(1, m.quantity - 1))} className="w-7 h-7 text-gray-600 font-bold">-</button>
                          <span className="w-6 text-center text-sm font-medium">{m.quantity}</span>
                          <button onClick={() => updateMedicationQuantity(idx, m.quantity + 1)} className="w-7 h-7 text-gray-600 font-bold">+</button>
                        </div>
                        <span className="text-sm font-semibold text-gray-800 w-16 text-right">${(m.total || 0).toFixed(2)}</span>
                        <button onClick={() => removeMedication(idx)} className="text-gray-400 hover:text-red-500 p-1">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  );
                })}
                {treatmentForm.medications.length === 0 && (
                  <p className="text-center text-gray-400 text-sm py-8">No medications added</p>
                )}
              </div>
            )}

            {/* Notes */}
            {activeManageTab === 'notes' && (
              <Textarea
                value={treatmentForm.notes}
                onChange={(e) => setTreatmentForm({ ...treatmentForm, notes: e.target.value })}
                placeholder="Add notes..."
                rows={10}
                className="text-sm bg-white border-gray-200 rounded-md resize-none"
                data-testid="visit-notes"
              />
            )}
          </div>

          {/* Fixed Footer */}
          <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-3 z-30">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-600">Total</span>
              <span className="text-lg font-bold text-gray-900">${treatmentTotal.toFixed(2)}</span>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={handleSaveTreatment} className="flex-1 h-10 text-sm rounded-md border-gray-300" data-testid="save-treatment-btn">
                Save
              </Button>
              <Button onClick={openConsentForm} className="flex-1 h-10 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-md" data-testid="submit-billing-btn">
                Submit for Billing
              </Button>
            </div>
          </div>
        </div>
      );
    }

    // ==================== FULL PAGE CONSENT FORM VIEW ====================
    if (doctorViewTab === 'consent' && selectedVisit) {
      return (
        <ConsentForm
          patientName={selectedVisit.patient_name || ''}
          services={treatmentForm.services}
          medications={treatmentForm.medications}
          onConsent={handleSubmitForBilling}
          onCancel={() => setDoctorViewTab('manage')}
        />
      );
    }

    // ==================== MAIN DOCTOR VIEW (Waiting / In Progress / History tabs) ====================
    return (
      <div className="min-h-screen bg-gray-50 -m-6 p-6" data-testid="visits-page">
        {/* App-like Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {doctorViewTab === 'waiting' ? 'Waiting' : doctorViewTab === 'inprogress' ? 'In Progress' : 'Bill History'}
            </h1>
            <p className="text-gray-500 text-sm">{workingLocation?.name}</p>
          </div>
          <div className="flex items-center gap-2">
            {(doctorViewTab === 'waiting' || doctorViewTab === 'inprogress') && (
              <span className={`px-4 py-2 rounded-full text-sm font-semibold ${
                (doctorViewTab === 'waiting' ? checkedInPatients.length : doctorInProgressPatients.length) > 0 
                  ? 'bg-blue-100 text-blue-700' 
                  : 'bg-gray-100 text-gray-600'
              }`}>
                {doctorViewTab === 'waiting' ? checkedInPatients.length : doctorInProgressPatients.length} patients
              </span>
            )}
          </div>
        </div>

        {/* Tab Switcher - Three Tabs */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setDoctorViewTab('waiting')}
            className={`flex-1 py-3 px-4 rounded-xl text-sm font-semibold transition-all active:scale-95 ${
              doctorViewTab === 'waiting'
                ? 'bg-blue-600 text-white shadow-lg'
                : 'bg-white text-gray-600 border border-gray-200'
            }`}
          >
            <Clock className="w-4 h-4 inline mr-1" />
            Waiting
            {checkedInPatients.length > 0 && (
              <span className={`ml-1 px-2 py-0.5 rounded-full text-xs ${
                doctorViewTab === 'waiting' ? 'bg-white/20' : 'bg-blue-100 text-blue-700'
              }`}>
                {checkedInPatients.length}
              </span>
            )}
          </button>
          <button
            onClick={() => setDoctorViewTab('inprogress')}
            className={`flex-1 py-3 px-4 rounded-xl text-sm font-semibold transition-all active:scale-95 ${
              doctorViewTab === 'inprogress'
                ? 'bg-gradient-to-r from-teal-600 to-blue-600 text-white shadow-lg'
                : 'bg-white text-gray-600 border border-gray-200'
            }`}
          >
            <UserCheck className="w-4 h-4 inline mr-1" />
            In Progress
            {doctorInProgressPatients.length > 0 && (
              <span className={`ml-1 px-2 py-0.5 rounded-full text-xs ${
                doctorViewTab === 'inprogress' ? 'bg-white/20' : 'bg-gray-200 text-gray-700'
              }`}>
                {doctorInProgressPatients.length}
              </span>
            )}
          </button>
          <button
            onClick={() => setDoctorViewTab('history')}
            className={`flex-1 py-3 px-4 rounded-xl text-sm font-semibold transition-all active:scale-95 ${
              doctorViewTab === 'history'
                ? 'bg-gradient-to-r from-teal-600 to-blue-600 text-white shadow-lg'
                : 'bg-white text-gray-600 border border-gray-200'
            }`}
          >
            <History className="w-4 h-4 inline mr-1" />
            History
            {pendingDoctorBills.length > 0 && (
              <span className={`ml-1 px-2 py-0.5 rounded-full text-xs ${
                doctorViewTab === 'history' ? 'bg-white/20' : 'bg-blue-100 text-blue-700'
              }`}>
                {pendingDoctorBills.length}
              </span>
            )}
          </button>
        </div>

        {/* WAITING TAB */}
        {doctorViewTab === 'waiting' && (
          <>
            {/* Search */}
            <div className="relative mb-6">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                placeholder="Search patients..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-12 h-12 text-base bg-white border-gray-200 rounded-xl"
                data-testid="search-visits"
              />
            </div>

            {/* Patient Cards - Waiting */}
            {loadingDoctorWaiting ? (
              <div className="flex items-center justify-center h-64">
                <div className="spinner"></div>
              </div>
            ) : checkedInPatients.length === 0 ? (
              <div className="bg-white rounded-2xl p-12 text-center">
                <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Clock className="w-8 h-8 text-blue-300" />
                </div>
                <p className="text-lg font-semibold text-gray-900">No patients waiting</p>
                <p className="text-gray-500 mt-1 text-sm">Patients will appear here after check-in</p>
              </div>
            ) : (
              <div className="space-y-3">
                {checkedInPatients.map((visit) => (
                  <div 
                    key={visit.id}
                    onClick={() => openTreatmentDialog(visit)}
                    className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100 cursor-pointer active:scale-[0.98] transition-transform"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center flex-shrink-0">
                        <span className="text-base font-bold text-blue-600">
                          {visit.patient_name?.split(' ').map(n => n[0]).join('').slice(0, 2)}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-gray-900 truncate">{visit.patient_name}</p>
                        <p className="text-gray-500 text-sm">
                          {new Date(visit.created_at).toLocaleDateString()} {new Date(visit.created_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 text-blue-600">
                        <span className="text-sm font-medium">Start</span>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* IN PROGRESS TAB */}
        {doctorViewTab === 'inprogress' && (
          <>
            {/* Search */}
            <div className="relative mb-6">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                placeholder="Search patients..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-12 h-12 text-base bg-white border-gray-200 rounded-xl"
                data-testid="search-visits-inprogress"
              />
            </div>

            {/* Patient Cards - In Progress */}
            {loadingDoctorInProgress ? (
              <div className="flex items-center justify-center h-64">
                <div className="spinner"></div>
              </div>
            ) : doctorInProgressPatients.length === 0 ? (
              <div className="bg-white rounded-2xl p-12 text-center">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-gray-300" />
                </div>
                <p className="text-lg font-semibold text-gray-900">No patients in progress</p>
                <p className="text-gray-500 mt-1 text-sm">Start with a patient from the Waiting tab</p>
              </div>
            ) : (
              <div className="space-y-3">
                {doctorInProgressPatients.map((visit) => (
                  <div 
                    key={visit.id}
                    onClick={() => openTreatmentDialog(visit)}
                    className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100 cursor-pointer active:scale-[0.98] transition-transform"
                  >
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center flex-shrink-0">
                        <span className="text-base font-bold text-gray-600">
                          {visit.patient_name?.split(' ').map(n => n[0]).join('').slice(0, 2)}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-gray-900 truncate">{visit.patient_name}</p>
                        <p className="text-gray-500 text-sm">
                          Started {new Date(visit.created_at).toLocaleDateString()} {new Date(visit.created_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 text-gray-600">
                        <span className="text-sm font-medium">Continue</span>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                    </div>
                    <div className="flex gap-4 text-xs text-gray-500 pl-15">
                      <span>{visit.services?.length || 0} services</span>
                      <span>{visit.medications?.length || 0} medications</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* BILL HISTORY TAB */}
        {doctorViewTab === 'history' && (
          <>
            {loadingDoctorHistory ? (
              <div className="flex items-center justify-center h-64">
                <div className="spinner"></div>
              </div>
            ) : doctorBills.length === 0 ? (
              <div className="bg-white rounded-2xl p-12 text-center">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FileText className="w-8 h-8 text-gray-300" />
                </div>
                <p className="text-lg font-semibold text-gray-900">No Bills Yet</p>
                <p className="text-gray-500 mt-1 text-sm">Bills you create will appear here</p>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Pending/Editable Bills */}
                {pendingDoctorBills.length > 0 && (
                  <div className="space-y-3">
                    <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider px-1">
                      Can Be Edited ({pendingDoctorBills.length})
                    </h3>
                    {pendingDoctorBills.map((bill) => (
                      <div 
                        key={bill.id}
                        className="bg-white rounded-3xl p-5 shadow-sm border border-gray-200"
                      >
                        <div className="flex items-center gap-4 mb-3">
                          <div className="w-14 h-14 bg-blue-50 rounded-2xl flex items-center justify-center flex-shrink-0">
                            <Receipt className="w-7 h-7 text-blue-600" />
                          </div>
                          <div className="flex-1">
                            <p className="font-bold text-gray-900 text-lg">{bill.patient_name}</p>
                            <p className="text-gray-500 text-sm">
                              B{bill.id?.slice(-8).toUpperCase()} • {new Date(bill.created_at).toLocaleDateString()}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="font-bold text-gray-900 text-xl">${(bill.total || 0).toFixed(2)}</p>
                            <span className={`text-xs px-2 py-1 rounded-full ${
                              bill.status === 'pending' ? 'bg-gray-100 text-gray-600' : 'bg-blue-100 text-blue-700'
                            }`}>
                              {bill.status === 'pending' ? 'Pending' : 'Partially Paid'}
                            </span>
                          </div>
                        </div>
                        
                        {/* Services/Medications summary */}
                        <div className="flex items-center justify-between">
                          <div className="flex gap-4 text-sm">
                            <div className="flex items-center gap-2 text-gray-600">
                              <Stethoscope className="w-4 h-4" />
                              <span>{bill.services?.length || 0} services</span>
                            </div>
                            <div className="flex items-center gap-2 text-gray-600">
                              <Pill className="w-4 h-4" />
                              <span>{bill.medications?.length || 0} medications</span>
                            </div>
                            {bill.revisions?.length > 0 && (
                              <div className="flex items-center gap-2 text-gray-500">
                                <RotateCcw className="w-4 h-4" />
                                <span>{bill.revisions.length} revision(s)</span>
                              </div>
                            )}
                          </div>

                          <Button 
                            onClick={() => openBillEditDialog(bill)}
                            className="h-10 px-6 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-xl active:scale-95 transition-transform"
                            data-testid={`edit-bill-${bill.id}`}
                          >
                            <Edit className="w-4 h-4 mr-2" />
                            Edit
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {/* Bill Edit Dialog (kept as dialog since it's for editing, not primary workflow) */}
        {renderBillEditDialog()}
      </div>
    );
  }

  // ==================== FRONT DESK / ADMIN VIEW - Tabs ====================
  return (
    <div className="space-y-6 animate-fade-in" data-testid="visits-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Visits</h1>
          <p className="text-gray-500 text-sm mt-1">
            {workingLocation?.name || 'All Locations'} • {filteredVisits.length} total visits
          </p>
        </div>
        
        {isFrontDesk() && (
          <p className="text-sm text-gray-500 bg-gray-100 px-4 py-2 rounded-lg">
            To check in patients, go to the <strong>Patients</strong> page
          </p>
        )}
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <Input
          placeholder="Search by patient or doctor name..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10 bg-white border-gray-200"
          data-testid="search-visits"
        />
      </div>

      {/* Status Tabs for Front Desk/Admin */}
      <Tabs 
        defaultValue="checked_in" 
        value={activeVisitsTab}
        onValueChange={(value) => {
          setActiveVisitsTab(value);
          // Lazy load data when tab is clicked
          if (value === 'in_progress' && !tabsLoaded.in_progress) {
            fetchInProgressVisits();
          } else if (value === 'completed' && !tabsLoaded.completed) {
            fetchCompletedVisits();
          }
        }}
        className="w-full"
      >
        <TabsList className="bg-slate-100">
          <TabsTrigger value="checked_in" className="data-[state=active]:bg-white flex items-center gap-2">
            <UserCheck className="w-4 h-4" />
            Waiting
            {visits.length > 0 && (
              <span className="ml-1 px-1.5 py-0.5 text-xs bg-slate-200 text-rose-600 rounded-full font-bold">
                {visits.length}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="in_progress" className="data-[state=active]:bg-white flex items-center gap-2">
            <Clock className="w-4 h-4" />
            In Progress
            {/* Show badge from stats API on initial load, then from actual data once tab is loaded */}
            {(tabsLoaded.in_progress ? inProgressTotalItems : initialInProgressCount) > 0 && (
              <span className="ml-1 px-1.5 py-0.5 text-xs bg-slate-200 text-rose-600 rounded-full font-bold">
                {tabsLoaded.in_progress ? inProgressTotalItems : initialInProgressCount}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="completed" className="data-[state=active]:bg-white flex items-center gap-2">
            <CheckCircle className="w-4 h-4" />
            Completed
          </TabsTrigger>
        </TabsList>

        <TabsContent value="checked_in" className="mt-6">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="spinner"></div>
            </div>
          ) : visits.length === 0 ? (
            <Card className="border-gray-200">
              <CardContent className="py-16">
                <div className="empty-state">
                  <UserCheck className="empty-state-icon" />
                  <p className="empty-state-title">No patients waiting</p>
                  <p className="empty-state-text">Patients will appear here once checked in</p>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Accordion type="single" collapsible className="space-y-2">
              {visits.map(renderVisitAccordion)}
            </Accordion>
          )}
        </TabsContent>

        <TabsContent value="in_progress" className="mt-6">
          {!tabsLoaded.in_progress ? (
            <div className="flex items-center justify-center h-64">
              <div className="spinner"></div>
            </div>
          ) : loadingInProgress ? (
            <div className="flex items-center justify-center h-64">
              <div className="spinner"></div>
            </div>
          ) : inProgressVisits.length === 0 ? (
            <Card className="border-gray-200">
              <CardContent className="py-16">
                <div className="empty-state">
                  <Clock className="empty-state-icon" />
                  <p className="empty-state-title">No visits in progress</p>
                  <p className="empty-state-text">Visits being treated will appear here</p>
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              <Accordion type="single" collapsible className="space-y-2">
                {inProgressVisits.map(renderVisitAccordion)}
              </Accordion>
              
              {/* Pagination for In Progress Visits - only show if > 10 items */}
              {inProgressTotalItems > 10 && (
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-4 border-t border-gray-200">
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <span>Showing {((inProgressPage - 1) * inProgressPageSize) + 1} - {Math.min(inProgressPage * inProgressPageSize, inProgressTotalItems)} of {inProgressTotalItems} in-progress visits</span>
                    <select
                      value={inProgressPageSize}
                      onChange={(e) => {
                        setInProgressPageSize(Number(e.target.value));
                        setInProgressPage(1);
                      }}
                      className="ml-2 px-2 py-1 border border-gray-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                    >
                      <option value={10}>10 per page</option>
                      <option value={20}>20 per page</option>
                      <option value={50}>50 per page</option>
                    </select>
                  </div>
                  
                  {inProgressTotalPages > 1 && (
                    <div className="flex items-center gap-1">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setInProgressPage(1)}
                        disabled={inProgressPage === 1}
                        className="px-2"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
                        </svg>
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setInProgressPage(p => Math.max(1, p - 1))}
                        disabled={inProgressPage === 1}
                        className="px-2"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                      </Button>
                      
                      <div className="flex items-center gap-1 mx-2">
                        {Array.from({ length: Math.min(5, inProgressTotalPages) }, (_, i) => {
                          let pageNum;
                          if (inProgressTotalPages <= 5) {
                            pageNum = i + 1;
                          } else if (inProgressPage <= 3) {
                            pageNum = i + 1;
                          } else if (inProgressPage >= inProgressTotalPages - 2) {
                            pageNum = inProgressTotalPages - 4 + i;
                          } else {
                            pageNum = inProgressPage - 2 + i;
                          }
                          return (
                            <Button
                              key={pageNum}
                              variant={inProgressPage === pageNum ? "default" : "outline"}
                              size="sm"
                              onClick={() => setInProgressPage(pageNum)}
                              className={`w-8 h-8 p-0 ${inProgressPage === pageNum ? 'bg-teal-600 text-white' : ''}`}
                            >
                              {pageNum}
                            </Button>
                          );
                        })}
                      </div>
                      
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setInProgressPage(p => Math.min(inProgressTotalPages, p + 1))}
                        disabled={inProgressPage === inProgressTotalPages}
                        className="px-2"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setInProgressPage(inProgressTotalPages)}
                        disabled={inProgressPage === inProgressTotalPages}
                        className="px-2"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
                        </svg>
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </TabsContent>

        <TabsContent value="completed" className="mt-6">
          {!tabsLoaded.completed ? (
            <div className="flex items-center justify-center h-64">
              <div className="spinner"></div>
            </div>
          ) : loadingCompleted ? (
            <div className="flex items-center justify-center h-64">
              <div className="spinner"></div>
            </div>
          ) : completedVisits.length === 0 ? (
            <Card className="border-gray-200">
              <CardContent className="py-16">
                <div className="empty-state">
                  <CheckCircle className="empty-state-icon" />
                  <p className="empty-state-title">No completed visits</p>
                  <p className="empty-state-text">Completed visits will appear here</p>
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              <Accordion type="single" collapsible className="space-y-2">
                {completedVisits.map(renderVisitAccordion)}
              </Accordion>
              
              {/* Pagination for Completed Visits */}
              {completedTotalItems > 0 && (
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-4 border-t border-gray-200">
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <span>Showing {((completedPage - 1) * completedPageSize) + 1} - {Math.min(completedPage * completedPageSize, completedTotalItems)} of {completedTotalItems} completed visits</span>
                    <select
                      value={completedPageSize}
                      onChange={(e) => {
                        setCompletedPageSize(Number(e.target.value));
                        setCompletedPage(1);
                      }}
                      className="ml-2 px-2 py-1 border border-gray-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                    >
                      <option value={10}>10 per page</option>
                      <option value={20}>20 per page</option>
                      <option value={50}>50 per page</option>
                    </select>
                  </div>
                  
                  {completedTotalPages > 1 && (
                    <div className="flex items-center gap-1">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCompletedPage(1)}
                        disabled={completedPage === 1}
                        className="px-2"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
                        </svg>
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCompletedPage(p => Math.max(1, p - 1))}
                        disabled={completedPage === 1}
                        className="px-2"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                      </Button>
                      
                      <div className="flex items-center gap-1 mx-2">
                        {Array.from({ length: Math.min(5, completedTotalPages) }, (_, i) => {
                          let pageNum;
                          if (completedTotalPages <= 5) {
                            pageNum = i + 1;
                          } else if (completedPage <= 3) {
                            pageNum = i + 1;
                          } else if (completedPage >= completedTotalPages - 2) {
                            pageNum = completedTotalPages - 4 + i;
                          } else {
                            pageNum = completedPage - 2 + i;
                          }
                          return (
                            <Button
                              key={pageNum}
                              variant={completedPage === pageNum ? "default" : "outline"}
                              size="sm"
                              onClick={() => setCompletedPage(pageNum)}
                              className={`w-8 h-8 p-0 ${completedPage === pageNum ? 'bg-teal-600 text-white' : ''}`}
                            >
                              {pageNum}
                            </Button>
                          );
                        })}
                      </div>
                      
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCompletedPage(p => Math.min(completedTotalPages, p + 1))}
                        disabled={completedPage === completedTotalPages}
                        className="px-2"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCompletedPage(completedTotalPages)}
                        disabled={completedPage === completedTotalPages}
                        className="px-2"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
                        </svg>
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Treatment Dialog */}
      {renderTreatmentDialog()}
      
      {/* Consent Dialog for Patient Signature */}
      <ConsentDialog
        open={consentDialogOpen}
        onOpenChange={setConsentDialogOpen}
        patientName={selectedVisit?.patient_name || ''}
        services={treatmentForm.services}
        medications={treatmentForm.medications}
        onConsent={handleSubmitForBilling}
      />

      {/* Cancel Visit Dialog */}
      <Dialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold text-slate-900 flex items-center gap-2">
              <XCircle className="w-5 h-5 text-red-500" />
              Cancel Visit
            </DialogTitle>
          </DialogHeader>
          {cancellingVisit && (
            <div className="space-y-4 mt-3 px-2">
              <div className="text-center">
                <p className="text-sm text-slate-500">Patient</p>
                <p className="font-semibold text-slate-900 text-base mt-0.5">{cancellingVisit.patient_name}</p>
              </div>
              <div className="text-sm">
                <p className="text-slate-500 mb-1.5">Cancellation Reason <span className="text-red-500">*</span></p>
                <textarea
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  placeholder="Enter the reason for cancellation..."
                  rows={2}
                  className="w-full rounded-lg border border-slate-300 px-4 py-3 text-sm focus:ring-2 focus:ring-red-400 focus:border-red-400 outline-none resize-none"
                  data-testid="cancel-reason-input"
                />
              </div>
              <div className="flex justify-end gap-2 pt-2 pb-3">
                <Button variant="outline" onClick={() => setCancelDialogOpen(false)} disabled={cancellingInProgress}>
                  Go Back
                </Button>
                <Button 
                  onClick={handleCancelVisit}
                  disabled={!cancelReason.trim() || cancellingInProgress}
                  className="bg-red-600 hover:bg-red-700 text-white"
                  data-testid="confirm-cancel-visit"
                >
                  {cancellingInProgress ? 'Cancelling...' : 'Cancel Visit'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

    </div>
  );
};

export default Visits;
